import React, { useEffect, useMemo, useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import { getInventoryCategoriesStorageKey, getInventorySettingsStorageKey } from '../utils/inventoryStorage';
import {
  fetchInventorySettings, saveInventorySettings, fromServerPayload, toServerPayload, writeLocalCache,
} from '../utils/inventorySettingsApi';
import {
  BUILT_IN_COLUMNS, OPTIONAL_EXTRA_COLUMNS, baseRequiredFields, defaultRequiredFields,
} from '../utils/inventoryFieldDefs';
import { INVENTORY_PRESETS, applyPreset } from '../utils/inventoryPresets';
import { PanelContainer, Title, Subtitle, PrimaryButton, SecondaryButton, IconButton, IconBadge } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { API_BASE } from '../config';
import { useConfirm } from '../components/ui/Modal';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// ─── Stałe danych ─────────────────────────────────────────────────────────────

// E.4: kanon kluczy kolumn wydzielony do utils/inventoryFieldDefs.js — jedno źródło
// prawdy dla tego komponentu i utils/inventoryPresets.js (test round-trip presetów
// nie może importować tego pliku wprost, bo ciągnie za sobą axios — patrz komentarz tam).
const equipmentColumns = BUILT_IN_COLUMNS;

// E.7: defaultRequiredFields przyjechało z kanonu (utils/inventoryFieldDefs.js) i jest
// SZERSZE niż baseRequiredFields — Kaucja i Numer inwentarzowy są nadal wymagane
// domyślnie, ale przestały być zablokowane.

// E.4: klucze zarezerwowane przez kolumny wbudowane/opcjonalne — kolumna własna presetu
// o takim kluczu jest przez utils/inventoryPresets.js#applyPreset pomijana (nigdy nie
// nadpisuje pola, które już istnieje jako wbudowane).
const RESERVED_COLUMN_KEYS = [...BUILT_IN_COLUMNS, ...OPTIONAL_EXTRA_COLUMNS].map(c => c.key);

// P8.1d: kubelek na sprzęt bez kategorii. To jest KLUCZ SKŁADOWANIA, a NIE napis — wędruje
// przez `byCategory` → `categoryRequirements`/`categories` i dalej `toServerPayload` do
// `PUT /api/inventory-settings` oraz do localStorage. Przetłumaczony zmieniałby się razem z
// językiem panelu, a `setCategoryRequirements` SUMUJE zamiast nadpisywać, więc każda wizyta w
// innym języku DOKŁADAŁA kolejną kategorię-widmo („Bez kategorii" + „No category" +
// „Ohne Kategorie") bez możliwości usunięcia ich z UI. Tłumaczy się dopiero przy renderze,
// przez `displayCategoryName` — ten sam wzorzec co `t(`inventory.type.${...}`)` w tabeli magazynu.
const UNCATEGORIZED_CATEGORY_KEY = 'Bez kategorii';

// E.7: `size` i `price_over_7` DOSZŁY tutaj przy audycie fazy E. Bez nich obietnica z E.5
// („wymagalność brand/size per organizacja przez konfigurator") i lista przełączalnych
// kolumn stałych w imporcie (lib/importSheet.js#OVERRIDABLE_REQUIRED_FIELDS) były
// nieosiągalne z produktu: `size` i `price_over_7` figurowały wyłącznie w
// `optionalCategoryFields`, czyli w wymaganiach PER KATEGORIA, których import z zasady
// nie czyta. Ta lista musi pokrywać OVERRIDABLE_REQUIRED_FIELDS, inaczej backend
// obsługuje przypadek, którego nie da się wyklikać.
const requiredFieldOptions = [
  { key: 'name',             labelKey: 'addEquipment.name',            descriptionKey: 'inventoryFields.name.descAlt' },
  { key: 'brand',            labelKey: 'addEquipment.brand',           descriptionKey: 'inventoryFields.brand.descAlt' },
  { key: 'size',             labelKey: 'addEquipment.size',            descriptionKey: 'inventoryFields.size.desc' },
  { key: 'price',            labelKey: 'addEquipment.price',           descriptionKey: 'inventoryFields.price.descAlt' },
  { key: 'price_over_7',     labelKey: 'addEquipment.priceOver7',      descriptionKey: 'inventoryFields.price_over_7.desc' },
  { key: 'bail',             labelKey: 'addEquipment.bail',            descriptionKey: 'inventoryFields.bail.descAlt' },
  { key: 'inventory_number', labelKey: 'addEquipment.inventoryNumber', descriptionKey: 'inventoryFields.inventory_number.descAlt' },
  { key: 'typeId',           labelKey: 'addEquipment.type',            descriptionKey: 'inventoryFields.typeId.desc' },
  { key: 'sku',              labelKey: 'addEquipment.sku',             descriptionKey: 'inventoryFields.sku.descAlt' },
  { key: 'condition',        labelKey: 'addEquipment.condition',       descriptionKey: 'inventoryFields.condition.descAlt' },
  { key: 'location',         labelKey: 'addEquipment.location',        descriptionKey: 'inventoryFields.location.desc' },
  { key: 'tags',             labelKey: 'addEquipment.tags',            descriptionKey: 'inventoryFields.tags.descAlt' },
  { key: 'supplier',         labelKey: 'addEquipment.supplier',        descriptionKey: 'inventoryFields.supplier.desc' },
  { key: 'purchaseDate',     labelKey: 'addEquipment.purchaseDate',    descriptionKey: 'inventoryFields.purchaseDate.desc' },
  { key: 'maintenanceDate',  labelKey: 'addEquipment.maintenanceDate', descriptionKey: 'inventoryFields.maintenanceDate.desc' },
  { key: 'images',           labelKey: 'addEquipment.images',          descriptionKey: 'inventoryFields.images.descAlt' },
  { key: 'barcode',          labelKey: 'addEquipment.barcode',         descriptionKey: 'inventoryFields.barcode.desc' },
  { key: 'currency',         labelKey: 'addEquipment.currency',        descriptionKey: 'inventoryFields.currency.desc' },
  { key: 'wearLevel',        labelKey: 'addEquipment.wearLevel',       descriptionKey: 'inventoryFields.wearLevel.desc' },
  { key: 'compatibility',    labelKey: 'addEquipment.compatibility',   descriptionKey: 'inventoryFields.compatibility.desc' },
  { key: 'sizeRange',        labelKey: 'addEquipment.sizeRange',       descriptionKey: 'inventoryFields.sizeRange.desc' },
];

const optionalCategoryFields = [
  { key: 'size',            labelKey: 'addEquipment.size',            descriptionKey: 'inventoryFields.size.descAlt' },
  { key: 'class',           labelKey: 'addEquipment.class',           descriptionKey: 'inventoryFields.class.desc' },
  { key: 'price_over_7',    labelKey: 'addEquipment.priceOver7',      descriptionKey: 'inventoryFields.price_over_7.descAlt' },
  { key: 'sku',             labelKey: 'addEquipment.sku',             descriptionKey: 'inventoryFields.sku.descAlt' },
  { key: 'condition',       labelKey: 'addEquipment.condition',       descriptionKey: 'inventoryFields.condition.descAlt' },
  { key: 'location',        labelKey: 'addEquipment.location',        descriptionKey: 'inventoryFields.location.desc' },
  { key: 'tags',            labelKey: 'addEquipment.tags',            descriptionKey: 'inventoryFields.tags.descAlt' },
  { key: 'supplier',        labelKey: 'addEquipment.supplier',        descriptionKey: 'inventoryFields.supplier.desc' },
  { key: 'purchaseDate',    labelKey: 'addEquipment.purchaseDate',    descriptionKey: 'inventoryFields.purchaseDate.desc' },
  { key: 'maintenanceDate', labelKey: 'addEquipment.maintenanceDate', descriptionKey: 'inventoryFields.maintenanceDate.desc' },
  { key: 'images',          labelKey: 'addEquipment.images',          descriptionKey: 'inventoryFields.images.descAlt' },
  { key: 'barcode',         labelKey: 'addEquipment.barcode',         descriptionKey: 'inventoryFields.barcode.desc' },
  { key: 'currency',        labelKey: 'addEquipment.currency',        descriptionKey: 'inventoryFields.currency.desc' },
  { key: 'wearLevel',       labelKey: 'addEquipment.wearLevel',       descriptionKey: 'inventoryFields.wearLevel.desc' },
  { key: 'compatibility',   labelKey: 'addEquipment.compatibility',   descriptionKey: 'inventoryFields.compatibility.desc' },
  { key: 'sizeRange',       labelKey: 'addEquipment.sizeRange',       descriptionKey: 'inventoryFields.sizeRange.desc' },
];

// ─── LocalStorage ─────────────────────────────────────────────────────────────

// Porównywalny zapis konfiguracji (stała kolejność pól — obiekty z różnych źródeł mają różną).
const snapshotConfig = (c) => JSON.stringify([
  c.visibleColumns || [], c.requiredFields || [], c.categoryRequirements || {}, c.categories || [], c.customColumns || [],
]);

const loadSettings = () => {
  if (typeof window === 'undefined') return {
    visibleColumns: equipmentColumns.map(c => c.key),
    requiredFields: defaultRequiredFields,
    categoryRequirements: {},
    categories: [],
    customColumns: [],
  };
  try {
    const settingsKey = getInventorySettingsStorageKey();
    const categoriesKey = getInventoryCategoriesStorageKey();
    const raw = window.localStorage.getItem(settingsKey);
    const parsed = raw ? JSON.parse(raw) : {};
    const visibleColumns = Array.isArray(parsed?.visibleColumns) && parsed.visibleColumns.length > 0
      ? parsed.visibleColumns : equipmentColumns.map(c => c.key);
    const requiredFieldsRaw = Array.isArray(parsed?.requiredFields) && parsed.requiredFields.length > 0
      ? parsed.requiredFields : defaultRequiredFields;
    const requiredFields = Array.from(new Set([...(requiredFieldsRaw || []), ...baseRequiredFields]));
    const categoryRequirements = parsed?.categoryRequirements || {};
    const customColumns = Array.isArray(parsed?.customColumns) ? parsed.customColumns : [];
    const rawCategories = parsed?.categories || window.localStorage.getItem(categoriesKey);
    const parsedCategories = Array.isArray(rawCategories) ? rawCategories : (() => {
      try { return rawCategories ? JSON.parse(rawCategories) : []; } catch { return []; }
    })();
    const categories = Array.from(new Set([
      ...(Array.isArray(parsedCategories) ? parsedCategories : []),
      ...Object.keys(categoryRequirements || {})
    ].map(v => String(v || '').trim()).filter(Boolean)));
    return { visibleColumns, requiredFields, categoryRequirements, categories, customColumns };
  } catch {
    return { visibleColumns: equipmentColumns.map(c => c.key), requiredFields: defaultRequiredFields, categoryRequirements: {}, categories: [], customColumns: [] };
  }
};

const persistSettings = (next) => {
  if (typeof window === 'undefined') return;
  try {
    const settingsKey = getInventorySettingsStorageKey();
    const categoriesKey = getInventoryCategoriesStorageKey();
    const categoryRequirements = next?.categoryRequirements || {};
    const categoryNames = Array.from(new Set([
      ...(Array.isArray(next?.categories) ? next.categories : []),
      ...Object.keys(categoryRequirements)
    ].map(v => String(v || '').trim()).filter(Boolean)));
    const payload = { ...next, categories: categoryNames };
    window.localStorage.setItem(settingsKey, JSON.stringify(payload));
    window.localStorage.setItem(categoriesKey, JSON.stringify(categoryNames));
  } catch {}
};

// ─── Ikony ────────────────────────────────────────────────────────────────────

const I = {
  Back: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  Reset: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>,
  Plus: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Columns: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>,
  Folder: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  Check: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  CheckAll: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  Uncheck: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
  Close: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  Edit: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Refresh: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  Trash: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  Info: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  Chevron: ({ up }) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={up ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}/></svg>,
  Tag: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  Inventory: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  Sparkle: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7z"/></svg>,
};

function csrfHeaders() {
  const csrf = (typeof window !== 'undefined' && window.localStorage.getItem('csrfToken')) || '';
  return { 'X-CSRF-Token': csrf };
}

// Nazwy typów, które potrafi założyć KTÓRYKOLWIEK zestaw startowy. Służą do jednego:
// odróżnienia typu „został po poprzednim zestawie" od typu, który firma dodała sobie sama.
// Tego drugiego zestaw startowy NIGDY nie rusza — nie ma go nawet na liście w podglądzie.
// Świadomie BEZ zapamiętywania, który zestaw był zastosowany: to wymagałoby migracji i kolumny,
// która rozjeżdża się z rzeczywistością przy każdej ręcznej zmianie słownika. Nazwy są i tak
// wypisane w oknie potwierdzenia, więc użytkownik widzi dokładnie, co znika.
const PRESET_TYPE_NAMES = new Set(
  INVENTORY_PRESETS.flatMap(p => p.suggestedTypes || []).map(n => String(n).trim().toLowerCase())
);

// Przewinięcie do elementu z poszanowaniem „ogranicz ruch" — animowane przewijanie jest tu
// miłe, ale dla kogoś z tą preferencją systemową ma być natychmiastowe, nie żadne.
function scrollElementIntoView(el, block, natychmiast = false) {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const ogranicz = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block, behavior: (natychmiast || ogranicz) ? 'auto' : 'smooth' });
}

// Redesign presetów (2026-09-22): każda branża ma własny znak rysowany z JEJ świata —
// narty, rower, wiosło, wiertarka, przyczepa, namiot. To jedyna ozdoba na kafelku i zarazem
// jedyny sposób, żeby sześć kart dało się odróżnić RZUTEM OKA, bez czytania nagłówków
// (wcześniej odróżniał je wyłącznie tekst, a resztę kafelka zjadała lista chipów z typami).
// Cieńsza kreska niż w `I` (1.6 vs 2): to ilustracja, nie kontrolka.
const PRESET_GLYPHS = {
  ski: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.6 20.4 16.4 5.6c.9-1.2 2.4-.5 2 1" />
      <path d="M19.4 20.4 7.6 5.6c-.9-1.2-2.4-.5-2 1" />
      <path d="M7.5 14.2 10 16.2" />
      <path d="M16.5 14.2 14 16.2" />
    </svg>
  ),
  bikes: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5.6" cy="16.6" r="3.6" />
      <circle cx="18.4" cy="16.6" r="3.6" />
      <path d="M5.6 16.6 10 7.8h5l3.4 8.8" />
      <path d="m10 7.8 2.7 8.8" />
      <path d="M13.6 7.8h3.3" />
    </svg>
  ),
  water: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.8 3.6 8.6 13.4" />
      <path d="m15.2 2.4 2.6 2.2" />
      <path d="M8.6 13.4c-1.8.7-2.9 2.2-2.4 3.2.6 1 2.4.8 3.7-.5 1.1-1.1 1.4-2.3.6-2.9z" />
      <path d="M2.4 19.4c1.6-1.5 3.2-1.5 4.8 0s3.2 1.5 4.8 0 3.2-1.5 4.8 0 3.2 1.5 4.8 0" />
    </svg>
  ),
  tools: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.6" y="6.6" width="10" height="6" rx="1.6" />
      <rect x="12.6" y="8" width="2.8" height="3.2" rx=".7" />
      <path d="M15.4 9.6H21" />
      <path d="M5.2 12.6 4 18.2a1.7 1.7 0 0 0 1.7 2.1h2.2l1.3-7.7" />
    </svg>
  ),
  vehicles: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6.6" y="5.8" width="13" height="7.6" rx="1.5" />
      <path d="m6.6 10 -4.2 2.3" />
      <circle cx="10.6" cy="17.4" r="2.3" />
      <circle cx="16.4" cy="17.4" r="2.3" />
      <path d="M12.9 17.4h1.2" />
    </svg>
  ),
  events: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.4 11.6 7.1 4.4l4.9 7.2 4.9-7.2 4.7 7.2" />
      <path d="M2.4 11.6v8.2M21.6 11.6v8.2M12 11.6v8.2" />
      <path d="M2.4 19.8h19.2" />
    </svg>
  ),
};

// Podgląd presetu wypisuje kolumny po ludzku (nie po kluczach) — ten sam `getColLabel`,
// którym renderuje się lista kolumn niżej, więc nazwa pola jest w obu miejscach ta sama.
const COLUMN_DEF_BY_KEY = new Map(
  [...BUILT_IN_COLUMNS, ...OPTIONAL_EXTRA_COLUMNS].map((c) => [c.key, c])
);

// ─── Główny komponent ─────────────────────────────────────────────────────────

export default function InventorySettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const initial = loadSettings();

  // Konfiguracja zapisuje się WYŁĄCZNIE po zmianie zrobionej przez użytkownika (12.10.2026).
  // Wczytywania (konfiguracja z serwera i wykrywanie kategorii przy wejściu) ustawiają stan
  // „surowymi" setterami `ustaw*`; wszystko inne idzie przez `set*` niżej, które oznaczają zmianę.
  // Dawniej auto-save wysyłał PUT 500 ms po KAŻDYM wczytaniu, także bez żadnej zmiany: samo
  // wejście na stronę odhaczało krok „Pola magazynu” w Pierwszych krokach, a zapis z karty
  // zostawionej przez test trafiał do bazy po jej resecie (inventory_settings_server.cy.js, CI).
  const [visibleColumns, ustawVisibleColumns]     = useState(initial.visibleColumns);
  const [requiredFields, ustawRequiredFields]     = useState(initial.requiredFields);
  const [categoryRequirements, ustawCategoryRequirements] = useState(initial.categoryRequirements || {});
  const [categories, ustawCategories]             = useState(initial.categories || []);
  const [customColumns, ustawCustomColumns]       = useState(initial.customColumns || []);
  const zmianaUzytkownikaRef = useRef(false);
  const zUzytkownika = (ustaw) => (wartosc) => { zmianaUzytkownikaRef.current = true; ustaw(wartosc); };
  const setVisibleColumns = zUzytkownika(ustawVisibleColumns);
  const setRequiredFields = zUzytkownika(ustawRequiredFields);
  const setCategoryRequirements = zUzytkownika(ustawCategoryRequirements);
  const setCategories = zUzytkownika(ustawCategories);
  const setCustomColumns = zUzytkownika(ustawCustomColumns);

  const [categoryName, setCategoryName]           = useState('');
  const [categoryExtraFields, setCategoryExtraFields] = useState([]);
  const [categoryFeedback, setCategoryFeedback]   = useState(null);

  const [categorySaving, setCategorySaving]       = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategoryKey, setEditingCategoryKey] = useState(null);
  const [inlineCreateOpen, setInlineCreateOpen]   = useState(false);

  const [expandedGroups, setExpandedGroups]       = useState({ basic: true, meta: false, pricing: true, custom: true });

  const [toast, setToast]                         = useState(null);
  const toastTimerRef                             = useRef(null);

  // E.4 / redesign 2026-09-22: preset działa w trzech krokach — wybierz branżę, sprawdź co
  // ustawi, zastosuj. Klik kafelka TYLKO zaznacza; nic nie zapisuje i nic nie wysyła.
  const [selectedPresetKey, setSelectedPresetKey] = useState(null);
  // JAWNE wybory użytkownika w liście typów w podglądzie: nazwa -> ma istnieć po zastosowaniu.
  // Brak klucza znaczy „wartość domyślna dla tego rodzaju wpisu" (patrz `presetTypePlan`), więc
  // zmiana listy `suggestedTypes` w zestawie nie wymaga niczego synchronizować.
  const [typeChoices, setTypeChoices]             = useState({});
  // Słownik typów TEJ organizacji tak, jak go zna serwer: `[{id, name}]`. Potrzebny z dwoma
  // powodami: `id` do kasowania i wiedza, które typy JUŻ istnieją (nie zakładamy ich drugi raz).
  // Źródło inne niż `categories` — tamto jest konfiguracją z `inventory_settings`, to jest bazą.
  const [serverCategories, setServerCategories]   = useState([]);
  // Nazwa kategorii w trakcie kasowania — blokada podwójnego kliknięcia.
  const [deletingCategory, setDeletingCategory]   = useState(null);
  // Przywracanie domyślnych kasuje kategorie po jednej, więc trwa — stąd blokada przycisku.
  const [resettingDefaults, setResettingDefaults] = useState(false);
  const [applyingPreset, setApplyingPreset]       = useState(false);
  // Podsumowanie po zastosowaniu. Trzyma kartę na ekranie, mimo że `categoryNames` przestaje
  // być puste w tej samej klatce — bez tego sekcja znikała dokładnie wtedy, gdy zestaw się
  // udał, co czytało się jak kolejne „kliknąłem i nic się nie stało".
  const [presetDone, setPresetDone]               = useState(null);
  // Czy karta „Zestaw startowy" jest w ogóle na tej stronie. Decyzja zapada RAZ, gdy strona
  // skończy się wczytywać — i trzyma się do końca wizyty.
  //
  // Wcześniej warunek był liczony na każdym renderze z `categoryNames.length === 0`, przez co
  // karta ZNIKAŁA w tej samej klatce, w której zestaw zakładał pierwszy typ. Skutki: podsumowanie
  // nie miało się gdzie pokazać, a przycisk „Wybierz inną branżę" był martwy — czyścił
  // `presetDone` i razem z nim gasił całą kartę, czyli dokładnie to, co miał otworzyć.
  const [starterCardOpen, setStarterCardOpen]     = useState(false);
  const starterDecidedRef                         = useRef(false);
  const presetReviewRef                           = useRef(null);
  const presetDoneRef                             = useRef(null);

  // Custom column form state
  const [showAddColumn, setShowAddColumn]         = useState(false);
  const [newColLabel, setNewColLabel]             = useState('');
  const [newColDesc, setNewColDesc]               = useState('');
  const [newColExample, setNewColExample]         = useState('');
  const [newColFieldType, setNewColFieldType]     = useState('text');
  const [newColOptionsText, setNewColOptionsText] = useState('');
  const [newColError, setNewColError]             = useState('');

  // E.2: konfiguracja pól magazynu żyje teraz na serwerze — dopóki mount-fetch się nie
  // zakończy (patrz useEffect niżej), auto-save NIE wysyła PUT-a, żeby nie nadpisać
  // świeżo pobranego stanu serwera stanem sprzed pobrania (cache/defaults).
  const [serverSynced, setServerSynced]           = useState(false);

  // Wczytanie strony to DWA kroki, wykonywane PO KOLEI: konfiguracja z serwera, potem wykrywanie
  // kategorii z bazy. Do 2026-09-17 biegły równolegle i wynik zależał od tego, która odpowiedź
  // przyszła pierwsza (złapane przez inventory_presets.cy.js na CI i odtworzone opóźnieniem):
  // - preset kliknięty, zanim skończyło się wykrywanie, był po chwili KASOWANY — gałąź „pusta
  //   organizacja" zerowała widoczne kolumny i wymagane pola, a auto-save zapisywał to na serwerze;
  // - spóźniona odpowiedź serwera nadpisywała kategorie dopiero co wykryte z bazy.
  // Dopóki oba kroki się nie skończą, kafelki presetów są nieaktywne: pokazywany stan jest jeszcze
  // przejściowy, więc ani „czy pytać o zastąpienie", ani samo zastąpienie nie mają na czym się oprzeć.
  const [initialSyncDone, setInitialSyncDone]     = useState(false);
  const serverConfiguredRef = useRef(false);
  const initialDetectStartedRef = useRef(false);
  // Konfiguracja w postaci, w jakiej zostawiły ją WYŁĄCZNIE wczytywania (cache/domyślne, potem serwer).
  // Różnica względem bieżącego stanu = zmiana użytkownika, której wykrywanie nie może skasować —
  // także zrobiona, ZANIM wykrywanie w ogóle wystartowało (czeka ono na odpowiedź serwera).
  const loaderSnapshotRef = useRef(snapshotConfig(initial));

  const saveTimeoutRef = useRef(null);
  const initialStateRef = useRef(initial);

  useEffect(() => {
    let cancelled = false;
    fetchInventorySettings()
      .then(data => {
        if (cancelled || !data?.configured) return;
        serverConfiguredRef.current = true;
        const mapped = fromServerPayload(data);
        loaderSnapshotRef.current = snapshotConfig(mapped);
        ustawVisibleColumns(mapped.visibleColumns);
        ustawRequiredFields(mapped.requiredFields);
        ustawCategoryRequirements(mapped.categoryRequirements);
        ustawCategories(mapped.categories);
        ustawCustomColumns(mapped.customColumns);
        writeLocalCache(mapped);
      })
      .catch(() => { /* zostaw dzisiejszy loadSettings() (cache/defaults) */ })
      .finally(() => { if (!cancelled) setServerSynced(true); });
    return () => { cancelled = true; };
  }, []);

  // `ms`: wyliczenie kategorii, których nie dało się usunąć, trzeba zdążyć przeczytać —
  // domyślne 3,5 s wystarcza na „Zapisano", ale nie na listę nazw z powodem.
  const showToast = (message, type = 'success', ms = 3500) => {
    setToast({ message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), ms);
  };

  // Auto-save (localStorage jako cache natychmiast; serwer — źródło prawdy — po debounsie).
  // Gated na serverSynced: dopóki mount-fetch nie ustali, co jest na serwerze, nie wolno
  // wysłać PUT-a ze stanem sprzed pobrania (nadpisałby świeżą konfigurację firmy).
  // Gated też na zmianę użytkownika (12.10.2026, patrz `zmianaUzytkownikaRef`): bez niej nic nie
  // trafia ani na serwer, ani do localStorage. Cache lokalny także, bo jednorazowa migracja
  // w App.js wysłałaby go na serwer przy następnym starcie, czyli samo wejście i tak by zapisało.
  useEffect(() => {
    if (!serverSynced || !zmianaUzytkownikaRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const next = { visibleColumns, requiredFields, categoryRequirements, categories, customColumns };
      persistSettings(next);
      saveInventorySettings(toServerPayload(next))
        .catch(() => {
          showToast(t('inventorySettings.saveServerFailed'), 'error');
        });
    }, 500);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [serverSynced, visibleColumns, requiredFields, categoryRequirements, categories, customColumns]);

  // Bieżąca konfiguracja dostępna w funkcjach asynchronicznych (aktualizowana przy każdym renderze).
  const configRef = useRef(null);
  configRef.current = { visibleColumns, requiredFields, categoryRequirements, categories, customColumns };

  // Wykrywanie kategorii przy wejściu na stronę — DOPIERO po konfiguracji z serwera (patrz wyżej).
  useEffect(() => {
    if (!serverSynced || initialDetectStartedRef.current) return;
    initialDetectStartedRef.current = true;
    detectCategoryFields({ onMount: true }).finally(() => setInitialSyncDone(true));
  }, [serverSynced]);

  // All columns (built-in + opcjonalne cenowe + custom)
  const allColumns = useMemo(() => {
    const customAsCols = customColumns.map(c => ({ ...c, group: 'custom' }));
    return [...BUILT_IN_COLUMNS, ...OPTIONAL_EXTRA_COLUMNS, ...customAsCols];
  }, [customColumns]);

  const basicColumns    = allColumns.filter(c => c.group === 'basic');
  const metaColumns     = allColumns.filter(c => c.group === 'meta');
  const pricingColumns  = allColumns.filter(c => c.group === 'pricing');
  const customColDefs   = allColumns.filter(c => c.group === 'custom');
  const basicColumnKeys = new Set(basicColumns.map(c => c.key));

  const assignedFields = useMemo(() => {
    try {
      const vals = Object.values(categoryRequirements || {});
      return new Set((vals || []).flat().filter(Boolean));
    } catch { return new Set(); }
  }, [categoryRequirements]);

  const categoryNames = useMemo(() => {
    return Array.from(new Set([
      ...(categories || []),
      ...Object.keys(categoryRequirements || {})
    ].map(v => String(v || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [categories, categoryRequirements]);

  // Karta zestawu startowego to skrót NA START: pokazujemy ją firmie, która nie ma jeszcze żadnej
  // kategorii sprzętu. Warunek sprawdzamy raz, po wczytaniu — późniejsze zmiany słownika (w tym
  // te zrobione tą kartą) nie mogą jej zamknąć w środku pracy. Przy następnym wejściu na stronę
  // organizacja ma już kategorie, więc karta się nie pojawia.
  useEffect(() => {
    if (!initialSyncDone || starterDecidedRef.current) return;
    starterDecidedRef.current = true;
    setStarterCardOpen(categoryNames.length === 0);
  }, [initialSyncDone, categoryNames.length]);

  const visibleCount = visibleColumns.length;
  const totalCount   = allColumns.length;

  // ── Handlers: Columns ────────────────────────────────────────────────────

  const handleToggleColumn = (key) => {
    setVisibleColumns(prev => {
      if (prev.includes(key)) {
        if (prev.length === 1) { showToast(t('inventorySettings.atLeastOneColumn'), 'error'); return prev; }
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleSelectGroup = (group) => {
    const keys = group.map(c => c.key);
    setVisibleColumns(prev => Array.from(new Set([...prev, ...keys])));
  };

  const handleDeselectGroup = (group) => {
    const keys = group.map(c => c.key);
    setVisibleColumns(prev => {
      const next = prev.filter(k => !keys.includes(k));
      return next.length > 0 ? next : [prev[0]];
    });
  };

  // ── Handlers: Custom columns ─────────────────────────────────────────────

  const handleAddCustomColumn = () => {
    setNewColError('');
    const label = newColLabel.trim();
    const desc  = newColDesc.trim();
    const example = newColExample.trim();
    if (!label) { setNewColError(t('inventorySettings.colNameRequired')); return; }
    if (label.length < 2) { setNewColError(t('inventorySettings.nameMin2')); return; }
    // Klucz liczony RAZ, przy tworzeniu — nigdy nie przeliczany z etykiety później
    // (E.2: dane w Equipment.metadata są zapisane pod tym kluczem, zmiana etykiety
    // nie może go ruszyć, inaczej dane "znikają" spod starego klucza).
    const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!key) { setNewColError(t('inventorySettings.nameOnlyInvalidChars')); return; }
    if (allColumns.some(c => c.key === key)) { setNewColError(t('inventorySettings.colExists').replace('{name}', key)); return; }
    const options = newColFieldType === 'list'
      ? newColOptionsText.split(',').map(o => o.trim()).filter(Boolean)
      : undefined;
    const newCol = {
      key, labelKey: null, label, description: desc || label, group: 'custom',
      example, fieldType: newColFieldType, options,
    };
    setCustomColumns(prev => [...prev, newCol]);
    setVisibleColumns(prev => [...prev, key]);
    setNewColLabel('');
    setNewColDesc('');
    setNewColExample('');
    setNewColFieldType('text');
    setNewColOptionsText('');
    setShowAddColumn(false);
    showToast(t('inventorySettings.colAdded').replace('{name}', label));
  };

  const handleDeleteCustomColumn = (key) => {
    setCustomColumns(prev => prev.filter(c => c.key !== key));
    setVisibleColumns(prev => prev.filter(k => k !== key));
    showToast(t('inventorySettings.colDeleted'), 'info');
  };

  // ── Handlers: Required fields ────────────────────────────────────────────

  const handleToggleRequired = (key) => {
    if (baseRequiredFields.includes(key)) return;
    setRequiredFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleToggleCategoryField = (key) => {
    setCategoryExtraFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // ── Handlers: Categories ─────────────────────────────────────────────────

  const openAddCategoryModal = () => {
    setCategoryName(''); setCategoryExtraFields([]); setEditingCategoryKey(null);
    setCategoryFeedback(null); setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (catName, extras) => {
    setCategoryName(catName); setCategoryExtraFields(extras);
    setEditingCategoryKey(catName); setCategoryFeedback(null); setIsCategoryModalOpen(true);
  };

  const handleAddCategory = async () => {
    const trimmed = String(categoryName || '').trim();
    if (!trimmed) { setCategoryFeedback({ type: 'error', message: t('inventorySettings.catNameRequired') }); return; }
    if (trimmed.length < 2) { setCategoryFeedback({ type: 'error', message: t('inventorySettings.nameMin2') }); return; }
    setCategorySaving(true);
    setCategoryFeedback({ type: 'info', message: t('inventorySettings.processing') });
    try {
      const catRes = await axios.get(`${API_BASE}/api/equipment-categories`, { withCredentials: true });
      const existing = Array.isArray(catRes.data) ? catRes.data.map(r => String(r.name).trim().toLowerCase()) : [];
      const existsOnServer = existing.includes(trimmed.toLowerCase());
      if (!existsOnServer) {
        // Nagłówek CSRF jest obowiązkowy: bez niego serwer odpowiada 403 (od 05.10.2026).
        await axios.post(`${API_BASE}/api/equipment-categories`, { name: trimmed }, {
          withCredentials: true, headers: csrfHeaders(),
        });
      }
      setCategoryRequirements(prev => ({ ...prev, [trimmed]: [...categoryExtraFields] }));
      setCategories(prev => Array.from(new Set([...(prev || []), trimmed])));
      setCategoryFeedback({ type: 'success', message: existsOnServer ? t('inventorySettings.catUpdated').replace('{name}', trimmed) : t('inventorySettings.catAdded').replace('{name}', trimmed) });
      setTimeout(() => { setCategoryFeedback(null); setIsCategoryModalOpen(false); setInlineCreateOpen(false); }, 1000);
    } catch (err) {
      const msg = err?.response?.data?.message || t('inventorySettings.catSaveFailed');
      setCategoryFeedback({ type: 'error', message: t('inventorySettings.errorPrefix').replace('{msg}', msg) });
    } finally { setCategorySaving(false); }
  };

  // ── Handlers: Presety branżowe (E.4, przeprojektowane 2026-09-22) ────────
  //
  // Dlaczego przeprojektowane: klik kafelka ustawiał POLA magazynu — czyli rzecz niewidoczną
  // z poziomu karty, bo lista kolumn leży niżej na stronie — a typy sprzętu, jedyną konkretną
  // rzecz, jaką kafelek pokazywał, trzeba było dokładać osobno, plusikiem przy każdym chipie.
  // Preset wyglądał więc na kliknięty i nieskuteczny („klikasz, potwierdzasz i kategorie się
  // nie dodają"). Teraz kafelek TYLKO zaznacza branżę, panel pod siatką wypisuje, co dokładnie
  // zostanie ustawione, a jeden podpisany przycisk robi obie rzeczy naraz.
  //
  // Zasada „preset sam z siebie nie POST-uje w tle" ZOSTAJE i jest MOCNIEJSZA niż była:
  // zapis wychodzi wyłącznie z jawnej akcji „Zastosuj zestaw", nigdy z samego zaznaczenia.

  // "Domyślna" konfiguracja = stan świeżej org (patrz loadSettings/defaults) — jeśli
  // użytkownik już ją zmienił (ręcznie albo innym presetem), zastosowanie zestawu pyta
  // o potwierdzenie, żeby nie skasować cichcem czyjejś pracy.
  const isDefaultInventoryConfig = () => {
    const sameVisible = visibleColumns.length === equipmentColumns.length
      && equipmentColumns.every(c => visibleColumns.includes(c.key));
    const sameRequired = requiredFields.length === defaultRequiredFields.length
      && defaultRequiredFields.every(f => requiredFields.includes(f));
    return sameVisible && sameRequired && customColumns.length === 0
      && categories.length === 0 && Object.keys(categoryRequirements || {}).length === 0;
  };

  const selectedPreset = useMemo(
    () => INVENTORY_PRESETS.find(p => p.key === selectedPresetKey) || null,
    [selectedPresetKey]
  );

  // Lista „jakie typy sprzętu mają istnieć PO zastosowaniu zestawu" — jedna reguła na cały
  // podgląd: **zaznaczone będą istnieć, odznaczone nie**. Stąd trzy rodzaje wpisów:
  //   • typ z wybranego zestawu          → domyślnie ZAZNACZONY (powstanie albo już jest),
  //   • typ zostawiony po POPRZEDNIM zestawie → domyślnie ODZNACZONY (zniknie),
  //   • typ dodany ręcznie przez firmę    → w ogóle go tu nie ma (nigdy nie ruszamy cudzej pracy).
  // Drugi rodzaj to cała poprawka z 2026-09-22: do tej pory zestaw tylko DOKŁADAŁ typy, więc
  // firma, która zmieniła branżę z „Kajaki i SUP" na „Narzędzia budowlane", miała w formularzu
  // dodawania sprzętu kategorie obu branż naraz i nie miała jak się ich pozbyć.
  const presetTypePlan = (preset) => {
    if (!preset) return [];
    const wZestawie = (preset.suggestedTypes || []).map(n => String(n).trim()).filter(Boolean);
    const wZestawieLower = new Set(wZestawie.map(n => n.toLowerCase()));
    const istniejace = new Map(
      (serverCategories || [])
        .filter(c => c && c.id != null)
        .map(c => [String(c.name || '').trim().toLowerCase(), c])
    );

    const plan = wZestawie.map(name => ({
      name,
      existingId: istniejace.get(name.toLowerCase())?.id ?? null,
      leftover: false,
    }));

    (serverCategories || []).forEach(c => {
      const nazwa = String(c?.name || '').trim();
      const lower = nazwa.toLowerCase();
      if (!nazwa || c.id == null) return;
      if (wZestawieLower.has(lower)) return;          // ten zestaw i tak go chce
      if (!PRESET_TYPE_NAMES.has(lower)) return;      // własny typ firmy — nie nasza sprawa
      plan.push({ name: nazwa, existingId: c.id, leftover: true });
    });

    return plan;
  };

  const isTypeChecked = (entry) => (
    Object.prototype.hasOwnProperty.call(typeChoices, entry.name) ? typeChoices[entry.name] : !entry.leftover
  );

  const handleSelectPreset = (preset) => {
    if (!preset || !initialSyncDone) return;
    setPresetDone(null);
    setSelectedPresetKey(prev => (prev === preset.key ? null : preset.key));
    // Wybór innej branży wraca do wartości domyślnych — wybory dotyczyły tamtej listy.
    setTypeChoices({});
  };

  const handleToggleSuggestedType = (entry) => {
    const teraz = isTypeChecked(entry);
    setTypeChoices(prev => ({ ...prev, [entry.name]: !teraz }));
  };

  // Usunięcie kategorii sprzętu. Do 2026-09-22 nie było TAKIEJ możliwości w całym produkcie:
  // backend miał wyłącznie POST i GET, więc literowka w nazwie zostawała na zawsze, a karta
  // zestawu startowego chowa się po pierwszym użyciu — bez tego przycisku sprzątanie leftoverów
  // byłoby osiągalne tylko w tej jednej wizycie, w której zestaw był zmieniany.
  const handleDeleteCategory = async (name) => {
    const nazwa = String(name || '').trim();
    if (!nazwa || deletingCategory) return;
    const wiersz = (serverCategories || []).find(
      c => String(c?.name || '').trim().toLowerCase() === nazwa.toLowerCase()
    );
    const ok = await confirm({
      title: t('inventorySettings.confirmDeleteCategory').replace('{name}', nazwa),
      message: t('inventorySettings.confirmDeleteCategoryBody'),
      tone: 'danger',
    });
    if (!ok) return;

    setDeletingCategory(nazwa);
    try {
      // Brak wiersza na serwerze = kategoria istnieje wyłącznie w konfiguracji (np. wykryta
      // z pola `category` sprzętu). Wtedy nie ma czego kasować w słowniku — znika sama z ustawień.
      if (wiersz) {
        await axios.delete(`${API_BASE}/api/equipment-categories/${wiersz.id}`, {
          withCredentials: true, headers: csrfHeaders(),
        });
      }
      setCategories(prev => (prev || []).filter(c => String(c).trim().toLowerCase() !== nazwa.toLowerCase()));
      setCategoryRequirements(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (String(k).trim().toLowerCase() === nazwa.toLowerCase()) delete next[k]; });
        return next;
      });
      await refreshServerCategories();
      // Wynik idzie w `categoryFeedback`, nie w `showToast`: toast renderuje się na GÓRZE strony,
      // a kasowanie klika się na karcie kategorii — czyli po kilku ekranach przewijania w dół.
      // Odmowa („kategoria jest używana") pokazana poza ekranem to dokładnie to samo „kliknąłem
      // i nic się nie stało", z którym ta sekcja walczy. Ta sama droga, co `catAdded`/`catUpdated`.
      setCategoryFeedback({ type: 'success', message: t('inventorySettings.catDeleted').replace('{name}', nazwa) });
      setTimeout(() => setCategoryFeedback(null), 3500);
    } catch (err) {
      if (err?.response?.status === 409) {
        const u = err.response.data?.usage || {};
        setCategoryFeedback({
          type: 'error',
          message: t('inventorySettings.catInUse')
            .replace('{name}', nazwa)
            .replace('{equipment}', String(u.equipment || 0))
            .replace('{rules}', String(u.priceRules || 0))
            .replace('{bundles}', String(u.bundles || 0)),
        });
      } else {
        setCategoryFeedback({ type: 'error', message: t('inventorySettings.catDeleteFailed') });
      }
      // Odmowa zostaje na ekranie do następnej akcji — użytkownik ma coś do zrobienia z tą
      // informacją (odpiąć sprzęt, regułę albo pakiet), więc nie gasimy jej po kilku sekundach.
    } finally {
      setDeletingCategory(null);
    }
  };

  // Słownik typów prosto z serwera. Wołany po każdej zmianie słownika, żeby `presetTypePlan`
  // i przycisk kasowania na karcie kategorii pracowały na prawdzie, a nie na stanie sprzed zapisu.
  const refreshServerCategories = async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/equipment-categories`, { withCredentials: true });
      const rows = (Array.isArray(r.data) ? r.data : []).filter(c => c && c.id != null);
      setServerCategories(rows);
      return rows;
    } catch (err) {
      return null;
    }
  };

  // Jedno kliknięcie = pola magazynu (stan lokalny, utrwalany istniejącym auto-save)
  // ORAZ doprowadzenie słownika typów do stanu z podglądu: zakładamy zaznaczone, których nie ma,
  // i kasujemy odznaczone, które są. Obie pętle idą SEKWENCYJNIE, nie przez Promise.all —
  // przy zakładaniu dwa typy różniące się tylko wielkością liter ścigałyby się o ten sam unikalny
  // indeks (uq_type_org_name, migracja 044), a przy kasowaniu chcemy wiedzieć, KTÓRY wpis odmówił.
  //
  // Kasowanie może zostać ODRZUCONE przez serwer (409 `category_in_use`) i to jest poprawny
  // wynik, nie awaria: na typie może stać sprzęt, reguła cennika albo pakiet. Takie typy
  // zostają, a podsumowanie mówi o tym wprost — zamiast udawać, że zeszły.
  const handleApplySelectedPreset = async () => {
    const preset = selectedPreset;
    if (!preset || !initialSyncDone || applyingPreset) return;

    const plan = presetTypePlan(preset);
    const doZalozenia = plan.filter(e => isTypeChecked(e) && !e.existingId).map(e => e.name);
    const doUsuniecia = plan.filter(e => !isTypeChecked(e) && e.existingId);

    if (!isDefaultInventoryConfig()) {
      const komunikat = doUsuniecia.length
        ? t('inventoryPresets.confirmReplaceBodyWithRemoval')
          .replace('{name}', t(preset.nameKey))
          .replace('{removed}', doUsuniecia.map(e => e.name).join(', '))
        : t('inventoryPresets.confirmReplaceBody').replace('{name}', t(preset.nameKey));
      const ok = await confirm({
        title: t('inventoryPresets.confirmReplace'),
        message: komunikat,
        confirmLabel: t('inventoryPresets.applyCta'),
        tone: doUsuniecia.length ? 'danger' : 'primary',
      });
      if (!ok) return;
    }

    setApplyingPreset(true);
    const result = applyPreset(preset, { visibleColumns, requiredFields, customColumns }, {
      baseRequiredFields, reservedKeys: RESERVED_COLUMN_KEYS,
    });
    setVisibleColumns(result.visibleColumns);
    setRequiredFields(result.requiredFields);
    setCustomColumns(result.customColumns);

    // Typy, które PO TEJ akcji na pewno istnieją — także gdy pętla padnie w połowie.
    const zalozone = [];
    const usuniete = [];
    const zablokowane = [];
    let bladSieci = false;

    try {
      for (const nazwa of doZalozenia) {
        await axios.post(`${API_BASE}/api/equipment-categories`, { name: nazwa }, {
          withCredentials: true, headers: csrfHeaders(),
        });
        zalozone.push(nazwa);
      }
    } catch (err) {
      // 409 przy zakładaniu = ktoś założył ten typ w międzyczasie. To nie jest błąd dla użytkownika:
      // stan końcowy jest taki, jakiego chciał — typ istnieje.
      if (err?.response?.status !== 409) bladSieci = true;
    }

    for (const wpis of doUsuniecia) {
      try {
        await axios.delete(`${API_BASE}/api/equipment-categories/${wpis.existingId}`, {
          withCredentials: true, headers: csrfHeaders(),
        });
        usuniete.push(wpis.name);
      } catch (err) {
        if (err?.response?.status === 409) zablokowane.push(wpis.name);
        else bladSieci = true;
      }
    }

    // Konfiguracja lokalna (sekcja „Kategorie sprzętu") musi pójść za słownikiem, inaczej
    // ustawienia pokazywałyby kategorie, których na serwerze już nie ma — i odwrotnie.
    const zostaje = plan.filter(e => isTypeChecked(e)).map(e => e.name);
    const znika = new Set(usuniete.map(n => n.toLowerCase()));
    setCategories(prev => Array.from(new Set([...(prev || []), ...zostaje]))
      .filter(n => !znika.has(String(n).trim().toLowerCase())));
    setCategoryRequirements(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (znika.has(String(k).trim().toLowerCase())) delete next[k]; });
      return next;
    });

    await refreshServerCategories();
    setApplyingPreset(false);

    if (bladSieci) {
      showToast(t('inventoryPresets.typeAddError'), 'error');
      return;
    }
    setPresetDone({
      name: t(preset.nameKey),
      fields: result.visibleColumns.length,
      types: zalozone.length,
      removed: usuniete.length,
      blocked: zablokowane,
    });
  };

  // Panel podglądu i panel podsumowania pojawiają się POD siatką, a strona bywa przewinięta
  // (sekcja kolumn jest długa). Bez tego „Zastosuj zestaw" kończy się tak samo jak stary preset:
  // coś się zapisało, ale potwierdzenia nie widać — zmierzone zrzutem ekranu, nie zgadywane.
  // `block: 'nearest'` nie rusza widoku, gdy panel i tak jest na ekranie.
  useEffect(() => {
    if (!selectedPresetKey || !presetReviewRef.current) return;
    scrollElementIntoView(presetReviewRef.current, 'nearest');
  }, [selectedPresetKey]);

  useEffect(() => {
    if (!presetDone || !presetDoneRef.current) return;
    // NATYCHMIAST, nie płynnie: w tej samej klatce siatka kafelków zamienia się w krótki panel
    // podsumowania, więc płynne przewijanie celuje w układ sprzed przebudowy i kończy obok.
    // Dwie klatki zwłoki dajemy przeglądarce na policzenie nowych wysokości (zmierzone zrzutem).
    const el = presetDoneRef.current;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollElementIntoView(el, 'start', true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [presetDone]);

  // options.onMount — wywołanie przy wejściu na stronę (ręczny przycisk „Wykryj" go nie przekazuje).
  const detectCategoryFields = async (options = {}) => {
    const onMount = options && options.onMount === true;
    // Wykrywanie przy wejściu to WCZYTYWANIE i niczego samo nie zapisuje; ręczny przycisk
    // „Wykryj” to decyzja użytkownika, więc jego wynik zapisuje się jak każda inna zmiana (12.10.2026).
    const ust = onMount
      ? { kolumny: ustawVisibleColumns, wymagane: ustawRequiredFields, wymagania: ustawCategoryRequirements, kategorie: ustawCategories }
      : { kolumny: setVisibleColumns, wymagane: setRequiredFields, wymagania: setCategoryRequirements, kategorie: setCategories };
    setCategoryFeedback({ type: 'info', message: t('inventorySettings.loadingFromDb') });
    try {
      const [resItems, resCats] = await Promise.all([
        axios.get(`${API_BASE}/api/equipment`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/equipment-categories`, { withCredentials: true })
      ]);
      const items = Array.isArray(resItems.data) ? resItems.data : [];
      const categoriesFromServer = Array.isArray(resCats.data) ? resCats.data : [];
      // Slownik typow tak, jak go widzi serwer (z `id`) — zrodlo dla `presetTypePlan`
      // i dla przycisku kasowania na karcie kategorii.
      setServerCategories(categoriesFromServer.filter(c => c && c.id != null));
      if (items.length === 0 && categoriesFromServer.length === 0) {
        // Zerowanie „pustej organizacji" przy wejściu na stronę NIE może skasować:
        // - konfiguracji zapisanej już na serwerze — inaczej firma bez sprzętu traciłaby wybrane
        //   kolumny przy każdym otwarciu ustawień;
        // - zmian zrobionych w trakcie wczytywania (przełączona kolumna itp.).
        // Ręczny przycisk „Wykryj" to świadoma decyzja użytkownika — zachowuje się jak dotąd.
        const changedByUser = snapshotConfig(configRef.current) !== loaderSnapshotRef.current;
        if (onMount && (serverConfiguredRef.current || changedByUser)) {
          setCategoryFeedback(null);
          return;
        }
        ust.kolumny([]); ust.wymagane([]); ust.wymagania({}); ust.kategorie([]);
        // Przy wejściu bez zapisu, także lokalnego: pusta konfiguracja w localStorage poszłaby
        // przy następnym starcie aplikacji na serwer jednorazową migracją z App.js (12.10.2026).
        if (!onMount) persistSettings({ visibleColumns: [], requiredFields: [], categoryRequirements: {}, categories: [], customColumns });
        setCategoryFeedback({ type: 'info', message: t('inventorySettings.noDataReset') });
        setTimeout(() => setCategoryFeedback(null), 4000); return;
      }
      if (items.length === 0 && categoriesFromServer.length > 0) {
        const names = categoriesFromServer.map(c => String(c?.name || '').trim()).filter(Boolean);
        ust.kategorie(prev => Array.from(new Set([...(prev || []), ...names])));
        ust.wymagania(prev => {
          const next = { ...prev };
          names.forEach(name => { if (!Array.isArray(next[name])) next[name] = []; });
          return next;
        });
        setCategoryFeedback({ type: 'info', message: t('inventorySettings.noEquipmentKeptCats') });
        setTimeout(() => setCategoryFeedback(null), 3000); return;
      }
      const propMap = { classId: 'class', price_over_7: 'price_over_7' };
      const byCategory = {};
      items.forEach(it => {
        const cat = it.category || UNCATEGORIZED_CATEGORY_KEY;
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(it);
      });
      const nextRequirements = {};
      Object.keys(byCategory).forEach(cat => {
        const rows = byCategory[cat];
        const detected = new Set();
        optionalCategoryFields.forEach(field => {
          const prop = propMap[field.key] || field.key;
          const has = rows.some(r => {
            const v = r[prop];
            if (v === null || v === undefined) return false;
            if (Array.isArray(v)) return v.length > 0;
            if (typeof v === 'string') return v.trim() !== '';
            return Boolean(v);
          });
          if (has) detected.add(field.key);
        });
        nextRequirements[cat] = Array.from(detected);
      });
      const serverCategoryNames = categoriesFromServer.map(c => String(c?.name || '').trim()).filter(Boolean);
      ust.kategorie(prev => Array.from(new Set([...(prev || []), ...serverCategoryNames, ...Object.keys(nextRequirements)])));
      ust.wymagania(prev => {
        const merged = { ...prev };
        Object.keys(nextRequirements).forEach(cat => {
          const detected = nextRequirements[cat] || [];
          const existing = Array.isArray(prev[cat]) ? prev[cat] : [];
          merged[cat] = Array.from(new Set([...existing, ...detected]));
        });
        return merged;
      });
      setCategoryFeedback({ type: 'success', message: t('inventorySettings.syncedWithDb') });
      setTimeout(() => setCategoryFeedback(null), 3500);
    } catch (err) {
      const status = err?.response?.status;
      setCategoryFeedback({ type: status === 401 ? 'error' : 'error', message: status === 401 ? t('inventorySettings.noAuthLogin') : t('inventorySettings.fetchFailed') });
    }
  };

  // Przywrócenie domyślnych = „zaczynamy magazyn od nowa": kolumny i pola wymagane wracają do
  // domyślnych, karta zestawu startowego się otwiera, a kategorie sprzętu są KASOWANE.
  //
  // Kasowanie kategorii jest tu świadome i żądane (2026-09-22), choć nie jest bezpieczne
  // z natury: firma, która dodawała własne kategorie miesiącami, traci je jednym kliknięciem.
  // Dlatego (a) okno potwierdzenia mówi to wprost i podaje LICZBĘ kategorii do usunięcia,
  // (b) lista jest dociagana ze serwera TUŹ PRZED pytaniem, żeby liczba nie kłamała,
  // (c) serwer odmawia (409) kategorii, na której stoi sprzęt, reguła cennika albo pakiet —
  //     takie zostają i są wymienione z nazwy, zamiast znikać po cichu albo wywalać reset.
  const handleResetToDefaults = async () => {
    if (resettingDefaults) return;

    const swieze = await refreshServerCategories();
    const doUsuniecia = (swieze || serverCategories || []).filter(c => c && c.id != null);

    const ok = await confirm({
      title: t('inventorySettings.confirmResetDefaults'),
      message: doUsuniecia.length
        ? t('inventorySettings.confirmResetDefaultsBodyWithCategories').replace('{count}', String(doUsuniecia.length))
        : t('inventorySettings.confirmResetDefaultsBody'),
      confirmLabel: t('inventorySettings.restoreDefaults'),
      tone: 'danger',
    });
    if (!ok) return;

    setResettingDefaults(true);
    setVisibleColumns(equipmentColumns.map(c => c.key));
    setRequiredFields(defaultRequiredFields);
    setCategoryRequirements({});

    // Karta „Zestaw startowy" WRACA. Widoczność jest osobnym stanem (patrz `starterCardOpen`),
    // rozstrzyganym raz po wczytaniu strony, więc trzeba ją otworzyć wprost — samo wyczyszczenie
    // konfiguracji tego nie robi, a bez tego reset zostawiał pusty magazyn bez drogi powrotnej.
    setPresetDone(null);
    setSelectedPresetKey(null);
    setTypeChoices({});
    setStarterCardOpen(true);

    // Sekwencyjnie, nie Promise.all: chcemy wiedzieć, KTÓRA kategoria odmówiła i dlaczego.
    const zablokowane = [];
    let bladSieci = false;
    for (const wpis of doUsuniecia) {
      try {
        await axios.delete(`${API_BASE}/api/equipment-categories/${wpis.id}`, {
          withCredentials: true, headers: csrfHeaders(),
        });
      } catch (err) {
        if (err?.response?.status === 409) zablokowane.push(String(wpis.name || '').trim());
        else bladSieci = true;
      }
    }

    // Lista kategorii MUSI pójść za serwerem, nie za założeniem „skasowaliśmy wszystko":
    // część kategorii mogła zostać. Gdy odczyt padnie, zostają te, o których wiemy na pewno.
    const rows = await refreshServerCategories();
    setCategories(rows
      ? rows.map(c => String(c?.name || '').trim()).filter(Boolean)
      : zablokowane.slice());
    setResettingDefaults(false);

    if (bladSieci) {
      showToast(t('inventorySettings.resetDefaultsPartial'), 'error', 9000);
      return;
    }
    if (zablokowane.length) {
      showToast(t('inventorySettings.resetDefaultsKept').replace('{names}', zablokowane.join(', ')), 'info', 9000);
      return;
    }
    showToast(t('inventorySettings.resetDefaultsDone'));
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const getColLabel = (col) => col.label || t(col.labelKey) || col.key;
  // Nazwy kategorii sa danymi organizacji i ida na serwer doslownie; tlumaczy sie WYLACZNIE
  // sentinel kubelka „bez kategorii", i to dopiero przy renderze.
  // Pula pól, które da się oznaczyć jako wymagane = wbudowane (`requiredFieldOptions`,
  // lista stała) PLUS kolumny własne tej organizacji. Do 2026-09-23 kolumny własne nie
  // miały jak tu trafić, choć CAŁA instalacja pod spodem już je obsługiwała:
  // `inventory_field_settings.required` istnieje od E.2, `toServerPayload`/`fromServerPayload`
  // liczą `required` dla każdego klucza bez wyjątku, a szablon i parser importu egzekwują
  // wymagalność pól własnych od E.3. Brakowało wyłącznie przełącznika.
  //
  // Etykieta kolumny własnej jest LITERALNA (dane organizacji), wbudowanej — kluczem i18n;
  // rozstrzyga to `getColLabel`/`getColDesc`, ten sam, którym renderuje się lista kolumn.
  const requiredFieldChoices = useMemo(() => ([
    ...requiredFieldOptions,
    ...(customColumns || [])
      .filter(c => c && c.key && !requiredFieldOptions.some(o => o.key === c.key))
      .map(c => ({ key: c.key, label: c.label || c.key, description: c.description || '', isCustom: true })),
  ]), [customColumns]);

  // Modal kategorii — dwie listy o RÓŻNYM ZASIĘGU, które do 2026-09-23 sobie przeczyły.
  //
  // „Pola wymagane" (sekcja wyżej na stronie) obowiązują W CAŁEJ organizacji i są
  // egzekwowane także przez import. „Dodatkowe pola" w tym modalu obowiązują WYŁĄCZNIE
  // dla sprzętu tej kategorii i import ich NIGDY nie czyta (decyzja z preambuły FAZY E:
  // jeden arkusz miesza typy, więc nagłówek nie może być „wymagany tylko dla wierszy
  // z typem Buty"). To NIE jest ten sam zbiór i nie ma po co go scalać.
  //
  // Błąd był gdzie indziej: pole wymagane globalnie (np. Stan sprzętu) pokazywało się tu
  // jako ODZNACZONY kwadracik, czyli dokładnie jako „wyłączone dla tej kategorii" — wprost
  // przeciwnie do ustawienia obok. Teraz takie pole stoi wśród zablokowanych chipów
  // „zawsze wymagane", a z listy do zaznaczania znika, bo nie ma tam czego decydować.
  const alwaysRequiredKeys = useMemo(
    () => Array.from(new Set([...baseRequiredFields, ...(requiredFields || [])])),
    [requiredFields]
  );

  // Pola do wyboru per kategoria: opcjonalne wbudowane PLUS kolumny własne organizacji
  // (zgłoszone jako brak 2026-09-23 — firma, która zakłada własne pole, chce móc wymagać
  // go dla jednej kategorii, a nie od razu dla całego magazynu), bez tych wymaganych
  // globalnie — te są obowiązkowe i tak.
  const categoryFieldChoices = useMemo(() => {
    const globalne = new Set(alwaysRequiredKeys);
    return [
      ...optionalCategoryFields,
      ...(customColumns || [])
        .filter(c => c && c.key && !optionalCategoryFields.some(o => o.key === c.key))
        .map(c => ({ key: c.key, label: c.label || c.key, description: c.description || '', isCustom: true })),
    ].filter(f => !globalne.has(f.key));
  }, [alwaysRequiredKeys, customColumns]);

  // Podglad zestawu liczy sie RAZ na render, nie trzy razy w JSX.
  const typePlan      = presetTypePlan(selectedPreset);
  const hasLeftovers  = typePlan.some(e => e.leftover);
  const toCreateCount = typePlan.filter(e => isTypeChecked(e) && !e.existingId).length;
  const toRemoveCount = typePlan.filter(e => !isTypeChecked(e) && e.existingId).length;

  const displayCategoryName = (name) =>
    (name === UNCATEGORIZED_CATEGORY_KEY ? t('inventorySettings.noCategory') : name);
  // Kolumna własna niesie literalny opis (dane organizacji, jak `label`), wbudowana — klucz i18n.
  const getColDesc = (col) => col.description || (col.descriptionKey ? t(col.descriptionKey) : '');

  // Podglad presetu: klucze kolumn -> etykiety widziane przez uzytkownika. Nieznany klucz
  // (preset starszy niz kanon kolumn) pokazuje sie jako surowy klucz zamiast znikac.
  const presetColumnLabels = (preset) =>
    (preset?.visibleColumns || []).map((key) => {
      const def = COLUMN_DEF_BY_KEY.get(key);
      return def ? getColLabel(def) : key;
    });

  const renderColumnGroup = (cols, groupKey, title, icon) => {
    const expanded = expandedGroups[groupKey] ?? true;
    const checkedCount = cols.filter(c => visibleColumns.includes(c.key)).length;

    return (
      <ColumnGroup key={groupKey} data-cy={`inventory-column-group-${groupKey}`}>
        <ColumnGroupHeader onClick={() => setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}>
          <ColumnGroupLeft>
            {icon}
            <ColumnGroupTitle>{title}</ColumnGroupTitle>
            <CountPill>{checkedCount}/{cols.length}</CountPill>
          </ColumnGroupLeft>
          <ColumnGroupRight>
            <MiniBtn onClick={e => { e.stopPropagation(); handleSelectGroup(cols); }} title={t('inventorySettings.selectAll')}>
              <I.CheckAll /> {t('common.all')}
            </MiniBtn>
            <MiniBtn onClick={e => { e.stopPropagation(); handleDeselectGroup(cols); }} title={t('inventorySettings.deselectAll')}>
              <I.Uncheck /> {t('inventorySettings.none')}
            </MiniBtn>
            <ChevronBtn><I.Chevron up={expanded} /></ChevronBtn>
          </ColumnGroupRight>
        </ColumnGroupHeader>

        {expanded && (
          <ColumnToggleGrid>
            {cols.map(col => {
              const isOn = visibleColumns.includes(col.key);
              const isCustom = col.group === 'custom';
              return (
                <ColumnToggleCard key={col.key} data-cy={`inventory-col-toggle-${col.key}`} $active={isOn} onClick={() => handleToggleColumn(col.key)}>
                  <ColumnToggleTop>
                    <Toggle $active={isOn}>
                      <ToggleKnob $active={isOn} />
                    </Toggle>
                    <ColumnToggleName $active={isOn}>{getColLabel(col)}</ColumnToggleName>
                    {isCustom && (
                      <CustomBadge onClick={e => { e.stopPropagation(); handleDeleteCustomColumn(col.key); }} title={t('inventorySettings.deleteThisColumn')}>
                        <I.Trash />
                      </CustomBadge>
                    )}
                  </ColumnToggleTop>
                  <ColumnToggleDesc>{getColDesc(col)}</ColumnToggleDesc>
                </ColumnToggleCard>
              );
            })}
          </ColumnToggleGrid>
        )}
      </ColumnGroup>
    );
  };

  return (
    <PanelContainer>

      {/* ── Nagłówek strony ─────────────────────────────────────── */}
      <PageHeader>
        <PageHeaderLeft>
          <IconButton onClick={() => navigate('/inventory')} aria-label={t('addEquipment.backToInventory')}>
            <ArrowLeftIcon />
          </IconButton>
          <TitleIcon>
            <I.Inventory />
          </TitleIcon>
          <div>
            <Title>{t('inventory.settings')}</Title>
            <Subtitle>{t('inventorySettings.subtitle')}</Subtitle>
          </div>
        </PageHeaderLeft>
        <TylkoOdczytWPrzerwie>
        <HeaderActions>
          <SecondaryButton
            data-cy="inventory-restore-defaults"
            disabled={resettingDefaults}
            onClick={handleResetToDefaults}
          >
            <I.Reset /> {resettingDefaults ? t('inventorySettings.restoringDefaults') : t('inventorySettings.restoreDefaults')}
          </SecondaryButton>
        </HeaderActions>
        </TylkoOdczytWPrzerwie>
      </PageHeader>
      <TylkoOdczytWPrzerwie>

      {/* ── Toast ───────────────────────────────────────────────── */}
      {toast && <Toast $type={toast.type}>{toast.message}</Toast>}

      {/* ── Sekcja: Zestaw startowy branży (E.4, redesign 2026-09-22) ────────
          Widoczna TYLKO dla świeżej org bez żadnej kategorii — to jednorazowy skrót na start,
          nie stały element strony. O widoczności PO wczytaniu rozstrzyga `starterCardOpen`,
          ustawiane RAZ (patrz komentarz przy stanie) — NIE `categoryNames.length === 0` liczone
          na każdym renderze, bo zestaw sam zakłada kategorie i gasiłby kartę w trakcie.
          W TRAKCIE wczytywania (`!initialSyncDone`) karta jest pokazywana OPTYMISTYCZNIE, z
          nieaktywnymi kafelkami (aria-disabled) — bez tego jej pierwsze pojawienie się na
          ekranie następowałoby dopiero PO zakończeniu synchronizacji, czyli kafelki byłyby od
          razu aktywne i nigdy nie dałoby się zobaczyć/przetestować stanu „ładowanie". */}
      {(!initialSyncDone || starterCardOpen) && (
        <SectionCard>
          <SectionCardHeader>
            <SectionCardIcon><I.Sparkle /></SectionCardIcon>
            <SectionCardText>
              <SectionCardTitle>{t('inventoryPresets.cardTitle')}</SectionCardTitle>
              <SectionCardSub>{t('inventoryPresets.cardSub')}</SectionCardSub>
            </SectionCardText>
          </SectionCardHeader>

          {presetDone ? (
            <PresetDonePanel ref={presetDoneRef} data-cy="inventory-preset-done">
              <PresetDoneMark aria-hidden="true"><I.Check /></PresetDoneMark>
              <PresetDoneText>
                <PresetDoneTitle>{t('inventoryPresets.doneTitle').replace('{name}', presetDone.name)}</PresetDoneTitle>
                <PresetDoneBody>
                  {t('inventoryPresets.doneSummary')
                    .replace('{types}', String(presetDone.types))
                    .replace('{fields}', String(presetDone.fields))}
                  {presetDone.removed > 0
                    ? ` ${t('inventoryPresets.doneRemoved').replace('{removed}', String(presetDone.removed))}`
                    : ''}
                  {presetDone.blocked && presetDone.blocked.length > 0
                    ? ` ${t('inventoryPresets.doneBlocked').replace('{names}', presetDone.blocked.join(', '))}`
                    : ''}
                </PresetDoneBody>
              </PresetDoneText>
              <PresetDoneActions>
                <PrimaryButton type="button" onClick={() => navigate('/inventory/add')}>
                  {t('inventoryPresets.doneAddEquipment')}
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => { setPresetDone(null); setSelectedPresetKey(null); setTypeChoices({}); }}
                >
                  {t('inventoryPresets.donePickAnother')}
                </SecondaryButton>
              </PresetDoneActions>
            </PresetDonePanel>
          ) : (
            <>
              <PresetGrid role="radiogroup" aria-label={t('inventoryPresets.cardTitle')}>
                {INVENTORY_PRESETS.map(preset => {
                  const Glyph = PRESET_GLYPHS[preset.key] || I.Sparkle;
                  const fieldCount = preset.visibleColumns.length + preset.customColumns.length;
                  const selected = selectedPresetKey === preset.key;
                  return (
                    <PresetTile
                      key={preset.key}
                      data-cy={`inventory-preset-${preset.key}`}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-disabled={initialSyncDone ? 'false' : 'true'}
                      aria-busy={initialSyncDone ? 'false' : 'true'}
                      $selected={selected}
                      $disabled={!initialSyncDone}
                      title={initialSyncDone ? undefined : t('inventorySettings.loadingFromDb')}
                      onClick={() => handleSelectPreset(preset)}
                    >
                      <PresetGlyph $selected={selected} aria-hidden="true"><Glyph /></PresetGlyph>
                      <PresetTileName>{t(preset.nameKey)}</PresetTileName>
                      <PresetTileDesc>{t(preset.descriptionKey)}</PresetTileDesc>
                      <PresetTileMeta>
                        {t('inventoryPresets.tileMeta')
                          .replace('{types}', String(preset.suggestedTypes.length))
                          .replace('{fields}', String(fieldCount))}
                      </PresetTileMeta>
                      <PresetMark aria-hidden="true"><I.Check /></PresetMark>
                    </PresetTile>
                  );
                })}
              </PresetGrid>

              {selectedPreset && (
                <PresetReview ref={presetReviewRef} data-cy="inventory-preset-review">
                  <PresetReviewTitle>
                    {t('inventoryPresets.reviewTitle').replace('{name}', t(selectedPreset.nameKey))}
                  </PresetReviewTitle>

                  {typePlan.length > 0 && (
                    <PresetReviewBlock>
                      <PresetReviewLabel>{t('inventoryPresets.reviewTypes')}</PresetReviewLabel>
                      <PresetReviewHint>{t('inventoryPresets.reviewTypesHint')}</PresetReviewHint>
                      {hasLeftovers && (
                        <PresetReviewHint>{t('inventoryPresets.reviewLeftoverHint')}</PresetReviewHint>
                      )}
                      <PresetTypesList>
                        {typePlan.map(entry => {
                          const on = isTypeChecked(entry);
                          return (
                            <PresetTypeToggle
                              key={entry.name}
                              type="button"
                              data-cy="inventory-preset-type"
                              data-leftover={entry.leftover ? 'true' : 'false'}
                              aria-pressed={on}
                              $on={on}
                              onClick={() => handleToggleSuggestedType(entry)}
                            >
                              <PresetTypeBox $on={on} aria-hidden="true">{on ? <I.Check /> : null}</PresetTypeBox>
                              <PresetTypeLabel $on={on}>{entry.name}</PresetTypeLabel>
                              {entry.leftover && (
                                <PresetTypeNote>{t('inventoryPresets.leftoverTag')}</PresetTypeNote>
                              )}
                            </PresetTypeToggle>
                          );
                        })}
                      </PresetTypesList>
                    </PresetReviewBlock>
                  )}

                  <PresetReviewBlock>
                    <PresetReviewLabel>{t('inventoryPresets.reviewFields')}</PresetReviewLabel>
                    <PresetFieldList>{presetColumnLabels(selectedPreset).join(' · ')}</PresetFieldList>
                    {selectedPreset.customColumns.length > 0 && (
                      <PresetFieldList>
                        <PresetFieldLead>{t('inventoryPresets.reviewNewFields')}</PresetFieldLead>{' '}
                        {selectedPreset.customColumns.map(c => c.label).join(' · ')}
                      </PresetFieldList>
                    )}
                  </PresetReviewBlock>

                  <PresetReviewFooter>
                    <PrimaryButton
                      type="button"
                      data-cy="inventory-preset-apply"
                      disabled={applyingPreset || !initialSyncDone}
                      onClick={handleApplySelectedPreset}
                    >
                      {applyingPreset ? t('inventoryPresets.applying') : t('inventoryPresets.applyCta')}
                    </PrimaryButton>
                    <PresetReviewNote>
                      {(toRemoveCount > 0 ? t('inventoryPresets.applyNoteWithRemoval') : t('inventoryPresets.applyNote'))
                        .replace('{types}', String(toCreateCount))
                        .replace('{removed}', String(toRemoveCount))
                        .replace('{fields}', String(selectedPreset.visibleColumns.length + selectedPreset.customColumns.length))}
                    </PresetReviewNote>
                  </PresetReviewFooter>
                </PresetReview>
              )}
            </>
          )}
        </SectionCard>
      )}

      {/* ── Sekcja: Kolumny tabeli ───────────────────────────────── */}
      <SectionCard>
        <SectionCardHeader>
          <SectionCardIcon><I.Columns /></SectionCardIcon>
          <SectionCardText>
            <SectionCardTitle>{t('inventorySettings.visibleColumns')}</SectionCardTitle>
            <SectionCardSub>{t('inventorySettings.columnsHint')}</SectionCardSub>
          </SectionCardText>
          <SectionBadge>{t('inventorySettings.activeCount').replace('{visible}', String(visibleCount)).replace('{total}', String(totalCount))}</SectionBadge>
        </SectionCardHeader>

        <InfoBanner>
          <I.Info />
          <span>{t('inventorySettings.autoSaveHint')}</span>
        </InfoBanner>

        {renderColumnGroup(basicColumns,  'basic',   t('inventorySettings.groupBasic'), <I.Columns />)}
        {renderColumnGroup(metaColumns,   'meta',    t('inventorySettings.groupMeta'),    <I.Tag />)}
        {renderColumnGroup(pricingColumns,'pricing', t('inventorySettings.groupPricing'),         <I.Columns />)}
        {customColDefs.length > 0 && renderColumnGroup(customColDefs, 'custom', t('inventorySettings.groupCustom'), <I.Tag />)}

        {/* Dodaj własną kolumnę */}
        {showAddColumn ? (
          <AddColumnForm>
            <AddColumnFormTitle>
              <I.Plus /> {t('inventorySettings.newColumn')}
            </AddColumnFormTitle>
            <AddColumnFields>
              <AddColumnField>
                <AddColumnLabel>{t('inventorySettings.colNameLabel')}</AddColumnLabel>
                <AddColumnInput
                  autoFocus
                  data-cy="inventory-add-column-name"
                  placeholder={t('inventorySettings.colNamePlaceholder')}
                  value={newColLabel}
                  onChange={e => { setNewColLabel(e.target.value); setNewColError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddCustomColumn()}
                />
                {newColError && <AddColError>{newColError}</AddColError>}
                <AddColumnHint>Klucz bazy: {newColLabel.trim() ? newColLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || '-' : '-'}</AddColumnHint>
              </AddColumnField>
              <AddColumnField>
                <AddColumnLabel>{t('inventorySettings.colDescLabel')}</AddColumnLabel>
                <AddColumnInput
                  placeholder={t('inventorySettings.colDescPlaceholder')}
                  value={newColDesc}
                  onChange={e => setNewColDesc(e.target.value)}
                />
              </AddColumnField>
              <AddColumnField>
                <AddColumnLabel>{t('inventorySettings.colExampleLabel')}</AddColumnLabel>
                <AddColumnInput
                  data-cy="inventory-add-column-example"
                  placeholder={t('inventorySettings.colExamplePlaceholder')}
                  value={newColExample}
                  onChange={e => setNewColExample(e.target.value)}
                />
              </AddColumnField>
              <AddColumnField>
                <AddColumnLabel>{t('inventorySettings.fieldType')}</AddColumnLabel>
                <SimpleSelect
                  style={{ width: '100%' }}
                  value={newColFieldType}
                  onChange={val => setNewColFieldType(val)}
                  options={[
                    { value: 'text', label: t('inventorySettings.fieldTypeText') },
                    { value: 'number', label: t('inventorySettings.fieldTypeNumber') },
                    { value: 'date', label: t('common.date') },
                    { value: 'list', label: t('inventorySettings.fieldTypeList') },
                  ]}
                />
              </AddColumnField>
              {newColFieldType === 'list' && (
                <AddColumnField>
                  <AddColumnLabel>{t('inventorySettings.listValuesLabel')}</AddColumnLabel>
                  <AddColumnInput
                    placeholder={t('inventorySettings.listValuesPlaceholder')}
                    value={newColOptionsText}
                    onChange={e => setNewColOptionsText(e.target.value)}
                  />
                </AddColumnField>
              )}
            </AddColumnFields>
            <AddColumnActions>
              <SecondaryButton onClick={() => {
                setShowAddColumn(false);
                setNewColLabel(''); setNewColDesc(''); setNewColExample('');
                setNewColFieldType('text'); setNewColOptionsText(''); setNewColError('');
              }}>
                {t('common.cancel')}
              </SecondaryButton>
              <PrimaryButton data-cy="inventory-add-column-submit" onClick={handleAddCustomColumn} disabled={!newColLabel.trim()}>
                <I.Plus /> {t('inventorySettings.addColumn')}
              </PrimaryButton>
            </AddColumnActions>
          </AddColumnForm>
        ) : (
          <AddColumnTrigger data-cy="inventory-add-column-trigger" onClick={() => setShowAddColumn(true)}>
            <I.Plus />
            {t('inventorySettings.addCustomColumn')}
          </AddColumnTrigger>
        )}
      </SectionCard>

      {/* ── Sekcja: Pola wymagane (E.7) ──────────────────────────────
          Do E.7 `requiredFields` dało się zmienić WYŁĄCZNIE presetem branżowym —
          `handleToggleRequired` istniał, ale nie był podpięty pod żaden element UI,
          więc Kaucja i Numer były w praktyce nie do odznaczenia. Ta sekcja to zmienia,
          a że ta sama flaga steruje teraz gwiazdką w szablonie importu (E.6/E.7),
          jest to też jedyne miejsce, w którym firma decyduje o wymagalności w imporcie. */}
      <SectionCard>
        <SectionCardHeader>
          <SectionCardIcon><I.Check /></SectionCardIcon>
          <SectionCardText>
            <SectionCardTitle>{t('inventorySettings.requiredTitle')}</SectionCardTitle>
            <SectionCardSub>
              {t('inventorySettings.requiredHint')}
            </SectionCardSub>
          </SectionCardText>
          <SectionBadge>{t('inventorySettings.requiredCount').replace('{count}', String(requiredFields.length))}</SectionBadge>
        </SectionCardHeader>

        <InfoBanner>
          <I.Info />
          <span>
            {t('inventorySettings.alwaysRequiredHint')}
          </span>
        </InfoBanner>

        <ChipRow style={{ marginBottom: 14 }}>
          {baseRequiredFields.map(f => {
            const opt = requiredFieldChoices.find(o => o.key === f);
            return <LockedChip key={f}><I.Check /> {opt ? getColLabel(opt) : f}</LockedChip>;
          })}
        </ChipRow>

        <ColumnToggleGrid>
          {requiredFieldChoices.filter(o => !baseRequiredFields.includes(o.key)).map(opt => {
            const isOn = requiredFields.includes(opt.key);
            return (
              <ColumnToggleCard
                key={opt.key}
                data-cy={`inventory-required-toggle-${opt.key}`}
                // `$active` jest transientnym propem styled-components i NIE trafia do DOM,
                // więc stan przełącznika był niewidoczny dla testów (i dla czytnika ekranu).
                data-active={isOn ? 'true' : 'false'}
                $active={isOn}
                onClick={() => handleToggleRequired(opt.key)}
              >
                <ColumnToggleTop>
                  <Toggle $active={isOn}><ToggleKnob $active={isOn} /></Toggle>
                  <ColumnToggleName $active={isOn}>{getColLabel(opt)}</ColumnToggleName>
                  {opt.isCustom && <CustomFieldTag>{t('inventorySettings.ownFieldTag')}</CustomFieldTag>}
                </ColumnToggleTop>
                <ColumnToggleDesc>{getColDesc(opt)}</ColumnToggleDesc>
              </ColumnToggleCard>
            );
          })}
        </ColumnToggleGrid>
      </SectionCard>

      {/* ── Sekcja: Kategorie ────────────────────────────────────── */}
      <SectionCard>
        <SectionCardHeader>
          <SectionCardIcon><I.Folder /></SectionCardIcon>
          <SectionCardText>
            <SectionCardTitle>{t('inventorySettings.categoriesTitle')}</SectionCardTitle>
            <SectionCardSub>{t('inventorySettings.categoriesSub')}</SectionCardSub>
          </SectionCardText>
          <SectionHeaderActions>
            <SecondaryButton data-cy="inventory-category-detect" onClick={() => detectCategoryFields()}>
              <I.Refresh /> {t('inventorySettings.autodetect')}
            </SecondaryButton>
            <PrimaryButton data-cy="inventory-category-new" onClick={() => { setInlineCreateOpen(true); setEditingCategoryKey(null); setCategoryName(''); setCategoryExtraFields([]); }}>
              <I.Plus /> {t('inventorySettings.newCategory')}
            </PrimaryButton>
          </SectionHeaderActions>
        </SectionCardHeader>

        {categoryFeedback && (
          <FeedbackBanner $type={categoryFeedback.type} data-cy="inventory-category-feedback" data-type={categoryFeedback.type}>
            {categoryFeedback.type === 'success' && '✓ '}
            {categoryFeedback.type === 'error'   && '⚠ '}
            {categoryFeedback.type === 'info'    && 'ℹ '}
            {categoryFeedback.message}
          </FeedbackBanner>
        )}

        {/* Inline tworzenie kategorii */}
        {inlineCreateOpen && (
          <InlineCreateCard>
            <InlineCreateHeader>
              <InlineCreateTitle>{t('inventorySettings.newCategoryTitle')}</InlineCreateTitle>
              <CloseBtn onClick={() => setInlineCreateOpen(false)}><I.Close /></CloseBtn>
            </InlineCreateHeader>

            <StepRow>
              <StepNum>1</StepNum>
              <StepBody>
                <StepLabel>{t('inventorySettings.categoryNameLabel')} <Req>*</Req></StepLabel>
                <FormInput
                  autoFocus
                  data-cy="inventory-category-name"
                  value={categoryName}
                  onChange={e => setCategoryName(e.target.value)}
                  placeholder={t('inventorySettings.categoryNamePlaceholder')}
                />
              </StepBody>
            </StepRow>

            <StepRow>
              <StepNum>2</StepNum>
              <StepBody>
                <StepLabel>{t('inventorySettings.baseFields')} <LockedBadge>{t('inventorySettings.alwaysRequired')}</LockedBadge></StepLabel>
                <ChipRow>
                  {baseRequiredFields.map(f => {
                    const opt = requiredFieldOptions.find(o => o.key === f);
                    return <LockedChip key={f}><I.Check /> {t(opt?.labelKey) || f}</LockedChip>;
                  })}
                </ChipRow>
              </StepBody>
            </StepRow>

            <StepRow>
              <StepNum>3</StepNum>
              <StepBody>
                <StepLabelRow>
                  <StepLabel>{t('inventorySettings.extraRequiredFields')}</StepLabel>
                  <QuickGroup>
                    <MiniBtn onClick={() => setCategoryExtraFields(optionalCategoryFields.map(f => f.key))}><I.CheckAll /> {t('common.all')}</MiniBtn>
                    <MiniBtn onClick={() => setCategoryExtraFields([])}><I.Uncheck /> {t('inventorySettings.none')}</MiniBtn>
                  </QuickGroup>
                </StepLabelRow>
                <FieldCheckGrid>
                  {optionalCategoryFields.map(field => (
                    <FieldCheckCard
                      key={field.key}
                      $checked={categoryExtraFields.includes(field.key)}
                      onClick={() => handleToggleCategoryField(field.key)}
                    >
                      <FieldCheckbox
                        type="checkbox"
                        checked={categoryExtraFields.includes(field.key)}
                        readOnly
                      />
                      <span>{t(field.labelKey) || field.key}</span>
                    </FieldCheckCard>
                  ))}
                </FieldCheckGrid>
              </StepBody>
            </StepRow>

            <InlineFormActions>
              <SecondaryButton onClick={() => setInlineCreateOpen(false)}>{t('common.cancel')}</SecondaryButton>
              <PrimaryButton
                data-cy="inventory-category-add"
                onClick={async () => { await handleAddCategory(); }}
                disabled={categorySaving || !categoryName.trim()}
              >
                {categorySaving ? t('inventorySettings.saving') : <><I.Plus /> {t('inventorySettings.addCategory')}</>}
              </PrimaryButton>
            </InlineFormActions>
          </InlineCreateCard>
        )}

        {/* Kategorie */}
        {categoryNames.length === 0 && !inlineCreateOpen ? (
          <EmptyState>
            <EmptyIcon>
              <I.Folder />
            </EmptyIcon>
            <EmptyTitle>{t('inventorySettings.noCategories')}</EmptyTitle>
            <EmptyDesc>{t('inventorySettings.noCategoriesDesc')}</EmptyDesc>
            <PrimaryButton data-cy="inventory-category-create-first" onClick={() => { setInlineCreateOpen(true); setEditingCategoryKey(null); setCategoryName(''); setCategoryExtraFields([]); }}>
              <I.Plus /> {t('inventorySettings.createFirstCategory')}
            </PrimaryButton>
          </EmptyState>
        ) : (
          <CatGrid>
            {categoryNames.map((catName, index) => {
              const rawExtras = categoryRequirements[catName] || [];
              const extras = Array.from(new Set(rawExtras.map(k => k === 'classId' ? 'class' : k)));
              // Paleta do wizualnego rozróżniania kart kategorii (jak kolory wydarzeń w kalendarzu) —
              // celowo poza tokenami tonu (success/warning/...), bo nie niosą żadnego znaczenia statusu.
              const GRADIENTS = [
                'var(--primary)',
                '#f5576c',
                '#00f2fe',
                '#38f9d7',
                '#fee140',
                '#330867',
                '#fbc2eb',
                '#fcb69f',
              ];
              const grad = GRADIENTS[index % GRADIENTS.length];

              return (
                <CatCard key={catName}>
                  <CatCardStripe style={{ background: grad }} />
                  <CatCardBody>
                    <CatCardTop>
                      <CatIconCircle style={{ background: grad }}>
                        <I.Folder />
                      </CatIconCircle>
                      <CatCardName>{displayCategoryName(catName)}</CatCardName>
                    </CatCardTop>

                    <CatStats>
                      <CatStat>
                        <CatStatVal>{baseRequiredFields.length + extras.length}</CatStatVal>
                        <CatStatLabel>{t('inventorySettings.requiredTitle')}</CatStatLabel>
                      </CatStat>
                      <CatStatDivider />
                      <CatStat>
                        <CatStatVal $accent>{extras.length}</CatStatVal>
                        <CatStatLabel>{t('inventorySettings.additional')}</CatStatLabel>
                      </CatStat>
                    </CatStats>

                    {extras.length > 0 && (
                      <CatExtras>
                        {extras.slice(0, 4).map(key => {
                          const k = key === 'classId' ? 'class' : key;
                          const entry = requiredFieldOptions.find(r => r.key === k) || optionalCategoryFields.find(r => r.key === k);
                          return <CatExtraChip key={key}>{t(entry?.labelKey) || k}</CatExtraChip>;
                        })}
                        {extras.length > 4 && <CatExtraMore>+{extras.length - 4}</CatExtraMore>}
                      </CatExtras>
                    )}
                  </CatCardBody>
                  <CatCardFooter>
                    <CatEditBtn onClick={() => openEditCategoryModal(catName, extras)}>
                      <I.Edit /> {t('common.edit')}
                    </CatEditBtn>
                    <CatDeleteBtn
                      type="button"
                      data-cy={`inventory-category-delete-${catName}`}
                      disabled={deletingCategory === catName}
                      onClick={() => handleDeleteCategory(catName)}
                    >
                      <I.Trash /> {t('common.delete')}
                    </CatDeleteBtn>
                  </CatCardFooter>
                </CatCard>
              );
            })}
          </CatGrid>
        )}
      </SectionCard>

      {/* ── Modal edycji kategorii ───────────────────────────────── */}
      {isCategoryModalOpen && (
        <ModalOverlay onClick={() => setIsCategoryModalOpen(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>{editingCategoryKey ? t('inventorySettings.editCategory').replace('{name}', displayCategoryName(editingCategoryKey)) : t('inventorySettings.newCategory')}</ModalTitle>
              </div>
              <CloseBtn onClick={() => setIsCategoryModalOpen(false)}><I.Close /></CloseBtn>
            </ModalHead>

            <ModalBody data-cy="inventory-category-modal-body">
              {!editingCategoryKey && (
                <StepRow>
                  <StepNum>1</StepNum>
                  <StepBody>
                    <StepLabel>{t('inventorySettings.categoryNameLabel')} <Req>*</Req></StepLabel>
                    <FormInput
                      autoFocus
                      value={categoryName}
                      onChange={e => setCategoryName(e.target.value)}
                      placeholder={t('inventorySettings.categoryNamePlaceholderShort')}
                    />
                  </StepBody>
                </StepRow>
              )}

              <StepRow>
                <StepNum>{editingCategoryKey ? '1' : '2'}</StepNum>
                <StepBody>
                  <StepLabel>{t('inventorySettings.baseFields')} <LockedBadge>{t('inventorySettings.alwaysRequired')}</LockedBadge></StepLabel>
                  <ChipRow>
                    {alwaysRequiredKeys.map(f => {
                      const opt = requiredFieldChoices.find(o => o.key === f);
                      return <LockedChip key={f}><I.Check /> {opt ? getColLabel(opt) : f}</LockedChip>;
                    })}
                  </ChipRow>
                </StepBody>
              </StepRow>

              <StepRow>
                <StepNum>{editingCategoryKey ? '2' : '3'}</StepNum>
                <StepBody>
                  <StepLabelRow>
                    <StepLabel>{t('inventorySettings.extraFields')}</StepLabel>
                    <QuickGroup>
                      {/* „Wszystkie"/„Żadne" ruszają TYLKO pozycje widoczne na liście. Pole,
                          które w międzyczasie stało się wymagane globalnie, jest stąd ukryte,
                          ale zostaje w zapisie — po wyłączeniu wymagalności globalnej wraca
                          zaznaczone, zamiast cicho zniknąć z konfiguracji kategorii. */}
                      <MiniBtn onClick={() => setCategoryExtraFields(prev => Array.from(new Set([
                        ...(prev || []).filter(k => !categoryFieldChoices.some(f => f.key === k)),
                        ...categoryFieldChoices.map(f => f.key),
                      ])))}><I.CheckAll /> {t('common.all')}</MiniBtn>
                      <MiniBtn onClick={() => setCategoryExtraFields(prev => (prev || [])
                        .filter(k => !categoryFieldChoices.some(f => f.key === k))
                      )}><I.Uncheck /> {t('inventorySettings.none')}</MiniBtn>
                    </QuickGroup>
                  </StepLabelRow>
                  <StepHint>{t('inventorySettings.extraFieldsScope')}</StepHint>
                  <FieldCheckGrid data-cy="inventory-category-extra-fields">
                    {categoryFieldChoices.map(field => (
                      <FieldCheckCard
                        key={field.key}
                        $checked={categoryExtraFields.includes(field.key)}
                        onClick={() => handleToggleCategoryField(field.key)}
                      >
                        <FieldCheckbox type="checkbox" checked={categoryExtraFields.includes(field.key)} readOnly />
                        <span>{getColLabel(field)}</span>
                        {field.isCustom && <CustomFieldTag>{t('inventorySettings.ownFieldTag')}</CustomFieldTag>}
                      </FieldCheckCard>
                    ))}
                  </FieldCheckGrid>
                </StepBody>
              </StepRow>

              {categoryFeedback && (
                <FeedbackBanner $type={categoryFeedback.type} style={{ marginTop: 0 }}>
                  {categoryFeedback.type === 'success' && '✓ '}
                  {categoryFeedback.type === 'error'   && '⚠ '}
                  {categoryFeedback.type === 'info'    && 'ℹ '}
                  {categoryFeedback.message}
                </FeedbackBanner>
              )}
            </ModalBody>

            <ModalFooter>
              <SecondaryButton onClick={() => setIsCategoryModalOpen(false)}>{t('common.cancel')}</SecondaryButton>
              <PrimaryButton onClick={handleAddCategory} disabled={categorySaving || (!editingCategoryKey && !categoryName.trim())}>
                {categorySaving ? t('inventorySettings.saving') : (editingCategoryKey ? t('inventorySettings.saveChanges') : t('inventorySettings.createCategory'))}
              </PrimaryButton>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}

// ─── Styled Components ────────────────────────────────────────────────────────

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Page header
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

// Ikona hero strony — wspólna plakietka z ui/common.js (IconBadge); ton "pine" (świerk),
// spójnie z resztą obszaru Magazynu, patrz komentarz przy ICON_TONES w ui/common.js.
const TitleIcon = styled(IconBadge).attrs({ $size: 56, $tone: 'pine' })``;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Toast = styled.div`
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
  animation: ${slideDown} 0.35s ease-out;

  ${p => p.$type === 'success' && css`
    background: var(--success-bg);
    border: 1px solid var(--success-border);
    color: var(--success);
  `}
  ${p => p.$type === 'error' && css`
    background: var(--danger-bg);
    border: 1px solid var(--danger-border);
    color: var(--danger);
  `}
  ${p => p.$type === 'info' && css`
    background: var(--info-bg);
    border: 1px solid var(--info-border);
    color: var(--info);
  `}
`;

const FeedbackBanner = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 16px;
  animation: ${slideDown} 0.3s ease-out;

  ${p => p.$type === 'success' && css`background: var(--success-bg); border: 1px solid var(--success-border); color: var(--success);`}
  ${p => p.$type === 'error'   && css`background: var(--danger-bg); border: 1px solid var(--danger-border); color: var(--danger);`}
  ${p => p.$type === 'info'    && css`background: var(--info-bg); border: 1px solid var(--info-border); color: var(--info);`}
`;

// Section card
const SectionCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 20px;
  padding: 28px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px var(--shadow-color);
  animation: ${fadeUp} 0.4s ease-out both;
`;

const SectionCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SectionCardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(59,130,246,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--primary);
  border: 1px solid rgba(59,130,246,0.15);
`;

const SectionCardText = styled.div`
  flex: 1;
  min-width: 200px;
`;

const SectionCardTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 3px;
  letter-spacing: -0.015em;
`;

const SectionCardSub = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
`;

const SectionBadge = styled.span`
  padding: 5px 14px;
  background: rgba(59,130,246,0.1);
  border: 1px solid rgba(59,130,246,0.2);
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary);
  white-space: nowrap;
`;

const SectionHeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-left: auto;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: var(--info-bg);
  border: 1px solid var(--info-border);
  border-radius: 10px;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-bottom: 20px;

  svg { flex-shrink: 0; color: var(--info); }
`;

// E.4 / redesign 2026-09-22: Zestaw startowy branzy.
//
// Uklad to „wybierz -> sprawdz -> zastosuj": siatka kafelkow (radiogroup) NIC nie zapisuje,
// a caly zapis wisi na jednym przycisku w panelu podgladu pod nia. Chipy z typami sprzetu
// wyprowadzone z kafelkow do tego panelu — na kafelku byly lista, ktorej nie dalo sie
// uzyc jako calosci, a kazdy z ~30 plusikow byl osobnym, niepodpisanym zapisem.
// Kolumny WPROST, nie `auto-fill`: presetów jest sześć, więc auto-fill układał je 4+2 —
// poszarpany wiersz, w którym dwa ostatnie kafelki wyglądają jak resztówka. 3/2/1 dzieli sześć
// bez reszty przy każdej szerokości.
const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

// <button>, nie <div>: kafelek nie zawiera juz zadnych wlasnych przyciskow, wiec moze byc
// prawdziwa kontrolka — fokus, Spacja i Enter obsluguje przegladarka, bez recznego onKeyDown.
const PresetTile = styled.button`
  position: relative;
  text-align: left;
  font-family: inherit;
  cursor: ${({ $disabled }) => ($disabled ? 'progress' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  background: ${({ $selected }) => ($selected ? 'var(--info-bg)' : 'var(--panel-bg)')};
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--input-border)')};
  box-shadow: ${({ $selected }) => ($selected ? 'var(--focus-ring)' : 'none')};
  border-radius: var(--r-lg);
  padding: 16px 40px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease),
              box-shadow var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease);

  &:hover:not([aria-disabled='true']) {
    border-color: var(--primary);
    transform: translateY(-1px);
  }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }
`;

// Znak branzy: stonowany, dopoki kafelek nie jest wybrany — szesc kolorowych ikon obok
// siebie robi z siatki plame, a wybor przestaje byc widoczny.
const PresetGlyph = styled.span`
  display: block;
  margin-bottom: 6px;
  color: ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--text-muted)')};
  transition: color var(--dur-fast) var(--ease);

  svg { width: 30px; height: 30px; display: block; }
`;

const PresetTileName = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--text);
`;

const PresetTileDesc = styled.span`
  font-size: 0.78125rem;
  line-height: 1.45;
  color: var(--text-muted);
`;

// Liczby jako dane, nie jako zdanie: rzeczownik przed liczba omija polska odmiane
// („4 typy" kontra „6 typow"), wiec ten sam napis jest poprawny dla kazdego presetu.
const PresetTileMeta = styled.span`
  margin-top: auto;
  padding-top: 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
`;

const PresetMark = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  color: transparent;
  transition: background var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease),
              color var(--dur-fast) var(--ease);

  [aria-checked='true'] > & {
    border-color: var(--primary);
    background: var(--primary);
    color: var(--primary-contrast);
  }

  svg { width: 11px; height: 11px; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// Panel podgladu celowo CICHY: jedyny kolor w tej sekcji to przycisk „Zastosuj zestaw",
// zeby bylo widac, gdzie konczy sie ogladanie, a zaczyna zapis.
const PresetReview = styled.div`
  margin-top: 16px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  background: var(--card-bg);
  padding: 18px;
  animation: ${fadeUp} 0.25s ease-out both;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const PresetReviewTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--text);
`;

const PresetReviewBlock = styled.div`
  & + & { margin-top: 18px; }
`;

const PresetReviewLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 4px;
`;

const PresetReviewHint = styled.p`
  margin: 0 0 10px;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const PresetTypesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Odznaczony typ ma przekreslona etykiete — mowi wprost „tego nie zalozymy", zamiast
// zostawiac roznice samemu odcieniowi ramki.
const PresetTypeToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 13px 6px 9px;
  border-radius: var(--r-pill);
  font-family: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  background: ${({ $on }) => ($on ? 'var(--panel-bg)' : 'transparent')};
  border: 1px solid ${({ $on }) => ($on ? 'var(--primary)' : 'var(--input-border)')};
  transition: background var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);

  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const PresetTypeBox = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $on }) => ($on ? 'var(--primary)' : 'var(--input-border)')};
  background: ${({ $on }) => ($on ? 'var(--primary)' : 'transparent')};
  color: var(--primary-contrast);

  svg { width: 10px; height: 10px; }
`;

// Znacznik „z poprzedniego zestawu" — bez niego przekreślony chip wyglądałby jak coś, co
// użytkownik sam odznaczył przed chwilą, a nie jak pozostałość do posprzątania.
const PresetTypeNote = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-muted);
`;

const PresetTypeLabel = styled.span`
  color: ${({ $on }) => ($on ? 'var(--text)' : 'var(--text-muted)')};
  text-decoration: ${({ $on }) => ($on ? 'none' : 'line-through')};
`;

const PresetFieldList = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--text-muted);

  & + & { margin-top: 6px; }
`;

const PresetFieldLead = styled.strong`
  color: var(--text);
  font-weight: 600;
`;

const PresetReviewFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--input-border);
`;

const PresetReviewNote = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const PresetDonePanel = styled.div`
  /* Zapas nad panelem przy przewijaniu do niego: bez tego lada z nagłówkiem karty („Zestaw
     startowy…") zostaje nad krawędzią ekranu i panel wygląda jak urwany. */
  scroll-margin-top: 104px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  border: 1px solid var(--success-border);
  background: var(--success-bg);
  border-radius: var(--r-lg);
  padding: 18px;
  animation: ${fadeUp} 0.25s ease-out both;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const PresetDoneMark = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--success);
  color: var(--card-bg);
`;

const PresetDoneText = styled.div`
  flex: 1 1 260px;
  min-width: 0;
`;

const PresetDoneTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 4px;
`;

const PresetDoneBody = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const PresetDoneActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

// Column groups
const ColumnGroup = styled.div`
  margin-bottom: 16px;
  border: 1px solid var(--input-border);
  border-radius: 14px;
  overflow: hidden;

  &:last-of-type { margin-bottom: 0; }
`;

const ColumnGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--table-header-bg);
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
  flex-wrap: wrap;
  gap: 8px;

  &:hover { background: var(--table-row-hover); }
`;

const ColumnGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text);

  svg { color: var(--primary); flex-shrink: 0; }
`;

const ColumnGroupTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
`;

const CountPill = styled.span`
  padding: 2px 10px;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const ColumnGroupRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MiniBtn = styled.button`
  padding: 5px 10px;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 8px;
  font-size: 0.77rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  font-family: inherit;

  svg { width: 12px; height: 12px; }

  &:hover {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const ChevronBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: all 0.2s;

  &:hover { background: var(--table-row-hover); color: var(--text); }
`;

const ColumnToggleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  padding: 16px;
  background: var(--panel-bg);

  @media (max-width: 600px) { grid-template-columns: 1fr 1fr; }
`;

const ColumnToggleCard = styled.div`
  border: 1.5px solid ${p => p.$active ? 'var(--primary)' : 'var(--input-border)'};
  border-radius: 12px;
  padding: 12px 14px;
  background: ${p => p.$active ? 'rgba(59,130,246,0.04)' : 'var(--card-bg)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--shadow-color);
  }
`;

const ColumnToggleTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
  /* Znacznik „własne" doklejony za nazwą nie może jej ściskać ani wystawać poza kartę —
     nazwy pól własnych pisze firma i bywają długie. */
  flex-wrap: wrap;
`;

const Toggle = styled.div`
  width: 36px;
  height: 20px;
  border-radius: 20px;
  background: ${p => p.$active ? 'var(--primary)' : 'var(--input-border)'};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
`;

const ToggleKnob = styled.div`
  position: absolute;
  top: 2px;
  left: ${p => p.$active ? '18px' : '2px'};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: var(--shadow-sm);
  transition: left 0.2s;
`;

const ColumnToggleName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text)'};
  flex: 1;
`;

const CustomBadge = styled.button`
  padding: 4px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover { color: var(--danger); background: var(--danger-bg); }
`;

const ColumnToggleDesc = styled.p`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
`;

// Add custom column form
const AddColumnTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 18px;
  margin-top: 16px;
  border: 2px dashed var(--input-border);
  border-radius: 12px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  justify-content: center;
  font-family: inherit;

  svg { color: var(--primary); }

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: rgba(59,130,246,0.04);
  }
`;

const AddColumnForm = styled.div`
  margin-top: 16px;
  padding: 20px;
  background: rgba(59,130,246,0.04);
  border: 2px solid rgba(59,130,246,0.2);
  border-radius: 14px;
  animation: ${fadeUp} 0.25s ease-out;
`;

const AddColumnFormTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 16px;

  svg { color: var(--primary); }
`;

const AddColumnFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const AddColumnField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AddColumnLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
`;

const AddColumnInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  font-family: inherit;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  &::placeholder { color: var(--text-muted); opacity: 0.7; }
`;


const AddColError = styled.span`
  font-size: 0.78rem;
  color: var(--danger);
  font-weight: 500;
`;

const AddColumnHint = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const AddColumnActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

// Categories section
const CatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 4px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const CatCard = styled.div`
  border: 1px solid var(--input-border);
  border-radius: 16px;
  overflow: hidden;
  background: var(--card-bg);
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px var(--shadow-color);
    border-color: rgba(59,130,246,0.2);
  }
`;

const CatCardStripe = styled.div`
  height: 5px;
  width: 100%;
`;

const CatCardBody = styled.div`
  padding: 16px;
  flex: 1;
`;

const CatCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const CatIconCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { color: white; width: 20px; height: 20px; }
`;

const CatCardName = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  letter-spacing: -0.01em;
`;

const CatStats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--panel-bg);
  border-radius: 10px;
  margin-bottom: 12px;
`;

const CatStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 2px;
`;

const CatStatVal = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${p => p.$accent ? 'var(--primary)' : 'var(--text)'};
  letter-spacing: -0.03em;
`;

const CatStatLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
`;

const CatStatDivider = styled.div`
  width: 1px;
  height: 32px;
  background: var(--input-border);
`;

const CatExtras = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const CatExtraChip = styled.span`
  padding: 3px 8px;
  background: rgba(59,130,246,0.08);
  border: 1px solid rgba(59,130,246,0.15);
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--primary);
`;

const CatExtraMore = styled.span`
  padding: 3px 8px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const CatCardFooter = styled.div`
  padding: 10px 16px;
  border-top: 1px solid var(--input-border);
  background: var(--panel-bg);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CatEditBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--card-bg);
  border: 1.5px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: rgba(59,130,246,0.06);
  }
`;

// Kasowanie kategorii: wygląd jak „Edytuj", ale ostrzeżenie kolorem DOPIERO na najechaniu —
// czerwony przycisk stojący na stałe przy każdej karcie robi z siatki kategorii pole min.
// Znacznik przy polu pochodzącym z konfiguracji organizacji. Bez niego lista wymaganych
// mieszająca pola wbudowane z własnymi wygląda jak jeden, niezrozumiały worek.
// Zdanie o ZASIĘGU listy. Bez niego dwie listy nazw pól — jedna na stronie, druga w tym
// oknie — wyglądają jak to samo ustawienie wpisane dwa razy.
const StepHint = styled.p`
  margin: 0 0 10px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-muted);
`;

const CustomFieldTag = styled.span`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: var(--r-pill);
  border: 1px solid var(--input-border);
  background: var(--panel-bg);
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
`;

const CatDeleteBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--card-bg);
  border: 1.5px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: var(--danger);
    color: var(--danger);
    background: var(--danger-bg);
  }
  &:disabled { opacity: 0.55; cursor: default; }
`;

// Empty state
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px;
  text-align: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: var(--panel-bg);
  border: 1.5px dashed var(--input-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  opacity: 0.6;
  margin-bottom: 4px;

  svg { width: 28px; height: 28px; }
`;

const EmptyTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
`;

const EmptyDesc = styled.p`
  font-size: 0.9rem;
  color: var(--text-muted);
  max-width: 440px;
  line-height: 1.6;
  margin: 0 0 8px;
`;

// Inline create
const InlineCreateCard = styled.div`
  background: var(--panel-bg);
  border: 1.5px solid rgba(59,130,246,0.2);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.25s ease-out;
`;

const InlineCreateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const InlineCreateTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: all 0.2s;

  &:hover { background: var(--table-row-hover); color: var(--text); }
`;

const StepRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 20px;

  &:last-of-type { margin-bottom: 0; }
`;

const StepNum = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  box-shadow: var(--shadow-primary);
`;

const StepBody = styled.div`
  flex: 1;
`;

const StepLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const Req = styled.span`
  color: var(--danger);
`;

const LockedBadge = styled.span`
  padding: 2px 8px;
  background: var(--success-bg);
  border: 1px solid var(--success-border);
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--success);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.9rem;
  background: var(--input-bg);
  color: var(--text);
  font-family: inherit;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }

  &::placeholder { color: var(--text-muted); opacity: 0.7; }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const LockedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  background: var(--success-bg);
  border: 1px solid var(--success-border);
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--success);

  svg { width: 12px; height: 12px; }
`;

const QuickGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const FieldCheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
  /* Bez własnego przewijania: okno miało DWA zagnieżdżone paski (treść okna i ta siatka),
     więc część pól dawało się znaleźć tylko przez złapanie właściwego z nich. Przewija
     się cała treść okna, a ono samo ma już ograniczenie wysokości (max-height na pudełku). */
`;

const FieldCheckCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1.5px solid ${p => p.$checked ? 'var(--primary)' : 'var(--input-border)'};
  border-radius: 10px;
  background: ${p => p.$checked ? 'rgba(59,130,246,0.06)' : 'var(--card-bg)'};
  cursor: pointer;
  transition: all 0.15s;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text);

  &:hover { border-color: var(--primary); background: rgba(59,130,246,0.04); }
`;

const FieldCheckbox = styled.input`
  width: 15px;
  height: 15px;
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
`;

const InlineFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--input-border);
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-overlay);
  padding: 20px;
  animation: ${keyframes`from{opacity:0}to{opacity:1}`} 0.2s ease-out;
`;

const ModalBox = styled.div`
  background: var(--card-bg);
  border-radius: 20px;
  width: 100%;
  max-width: 580px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 32px 64px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05);
  animation: ${fadeUp} 0.3s ease-out;
  /* Przycinanie zawartości do zaokrąglonych rogów (tło nagłówka i stopki).
     Był tu też dekoracyjny pseudoelement przed nagłówkiem — 4-pikselowy pasek w kolorze
     obramowania. Nie niósł żadnej informacji, a że pudełko nie przycinało zawartości,
     sterczał nad zaokrąglonym rogiem i czytał się jak usterka renderowania.
     UWAGA: w tym literale NIE WOLNO używać znaku wstecznego apostrofu — zamyka szablon
     styled-components i wywraca kompilację kilkaset linii dalej. */
  overflow: hidden;
`;

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--input-border);
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
  letter-spacing: -0.02em;
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--input-border);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: var(--panel-bg);
  border-radius: 0 0 20px 20px;
`;
